<template>
  <div class="bqa">
    <header class="bqa-top">
      <div class="bqa-top__left">
        <router-link to="/" class="bqa-back">← Назад</router-link>
        <div>
          <h1 class="bqa-title">Диалоги бота</h1>
          <p class="bqa-subtitle">
            Исход, триггер и выжимку ставит ИИ. Можно открыть переписку и скопировать одним кликом.
          </p>
        </div>
      </div>
      <div class="bqa-top__actions">
        <button type="button" class="bqa-btn bqa-btn--ghost" :disabled="syncing" @click="runSync">
          {{ syncing ? 'Синхронизация…' : 'Подтянуть' }}
        </button>
        <button type="button" class="bqa-btn bqa-btn--ghost" @click="openEvalConfig">
          Промпт ИИ
        </button>
        <button type="button" class="bqa-btn bqa-btn--accent" :disabled="aiRunning" @click="runAiEval">
          {{ aiRunning ? 'Оценка…' : 'Оценить ИИ' }}
        </button>
      </div>
    </header>

    <section class="bqa-filters">
      <div class="bqa-filters__row">
        <div class="bqa-field">
          <span class="bqa-field__label">Период</span>
          <div class="bqa-period-bar">
            <button
              v-for="p in periodPresets"
              :key="p.value"
              type="button"
              class="bqa-period-chip"
              :class="{ 'is-active': periodPreset === p.value && !compareEnabled }"
              @click="applyPeriodPreset(p.value)"
            >{{ p.label }}</button>
            <button
              type="button"
              class="bqa-period-chip bqa-period-chip--compare"
              :class="{ 'is-active': compareEnabled || dateModalOpen }"
              @click="openDateModal(compareEnabled ? 'compare' : 'filter')"
            >
              {{ compareEnabled ? 'Сравнить · вкл.' : 'Сравнить' }}
              <span class="bqa-period-chip__caret">▾</span>
            </button>
            <button type="button" class="bqa-period-range" @click="openDateModal('filter')">
              {{ formatDay(dateFrom) }} — {{ formatDay(dateTo) }}
            </button>
          </div>
        </div>

        <div class="bqa-field">
          <span class="bqa-field__label">Тег ИИ (итог)</span>
          <div class="bqa-pills">
            <button
              v-for="opt in primaryTagOptions"
              :key="'pt-' + opt.value"
              type="button"
              class="bqa-pill"
              :class="{ 'is-active': primaryTagFilter === opt.value }"
              @click="setPrimaryTag(opt.value)"
            >{{ opt.label }}</button>
          </div>
        </div>

        <div class="bqa-field">
          <span class="bqa-field__label">Триггер</span>
          <div class="bqa-pills">
            <button
              v-for="opt in triggerOptions"
              :key="opt.value"
              type="button"
              class="bqa-pill"
              :class="{ 'is-active': trigger === opt.value }"
              @click="setTrigger(opt.value)"
            >{{ opt.label }}</button>
          </div>
        </div>

        <div class="bqa-field bqa-field--grow">
          <span class="bqa-field__label">Поиск</span>
          <div class="bqa-search">
            <input
              v-model="q"
              type="search"
              class="bqa-input bqa-input--search"
              placeholder="Телефон, dialog id, текст…"
              @keyup.enter="reload"
            />
            <button type="button" class="bqa-btn bqa-btn--primary" :disabled="loading" @click="reload">
              Найти
            </button>
          </div>
        </div>
      </div>

      <div class="bqa-filters__row bqa-filters__row--secondary">
        <select
          v-model="failureClass"
          class="bqa-input bqa-select"
          :disabled="outcome !== 'fail' && outcome !== 'all'"
          @change="reload"
        >
          <option value="">Причина fail — любая</option>
          <option value="dropout_after_first_reply">Ушёл после 1 реплики</option>
          <option value="clarify_no_prices">Уточнения без цен</option>
          <option value="prices_shown_ghosted">Цены показаны, пропал</option>
          <option value="callback_form_only">Только форма</option>
          <option value="empty">Пустой</option>
        </select>

        <select v-model="ratingFilter" class="bqa-input bqa-select bqa-select--rating" @change="reload">
          <option value="">Оценка ОК — любая</option>
          <option value="unrated">Без оценки ОК</option>
          <option value="rated">С оценкой ОК</option>
          <option value="1-2">1–2 ★</option>
          <option value="3">3 ★</option>
          <option value="4-5">4–5 ★</option>
        </select>

        <select v-model="tagFilter" class="bqa-input bqa-select" @change="onTagFilterChange">
          <option value="">Тег — любой</option>
          <optgroup label="Теги ИИ">
            <option v-for="t in clarifyingTagPresets" :key="'ai-' + t.code" :value="t.code">{{ t.label }}</option>
          </optgroup>
          <optgroup label="Ручные теги">
            <option v-for="t in manualTagPresets" :key="'man-' + t.code" :value="t.code">{{ t.label }}</option>
          </optgroup>
        </select>

        <label class="bqa-check">
          <input v-model="onlyHasPhone" type="checkbox" @change="reload" />
          Есть номер телефона
        </label>
        <label class="bqa-check">
          <input v-model="onlySold" type="checkbox" @change="reload" />
          С заявкой
        </label>
        <label class="bqa-check">
          <input v-model="onlyOrder" type="checkbox" @change="reload" />
          Есть заказ CRM
        </label>

        <span v-if="lastSync" class="bqa-meta">Синк: {{ formatIso(lastSync) }}</span>
      </div>
    </section>

    <section v-if="stats" class="bqa-metric-bar">
      <div ref="metricDropdown" class="bqa-metric-dd" :class="{ 'is-open': metricDropdownOpen }">
        <button
          type="button"
          class="bqa-metric-dd__btn"
          :aria-expanded="metricDropdownOpen ? 'true' : 'false'"
          @click.stop="metricDropdownOpen = !metricDropdownOpen"
        >
          <span class="bqa-metric-dd__btn-label">Метрики графика</span>
          <span class="bqa-metric-dd__btn-summary">{{ metricDropdownSummary }}</span>
          <span class="bqa-metric-dd__caret">{{ metricDropdownOpen ? '▴' : '▾' }}</span>
        </button>
        <div v-show="metricDropdownOpen" class="bqa-metric-dd__panel" role="menu">
          <div
            v-for="col in chartMetricColumns"
            :key="col.id"
            class="bqa-metric-dd__col"
          >
            <div class="bqa-metric-dd__col-title">{{ col.title }}</div>
            <button
              v-for="m in col.items"
              :key="m.key"
              type="button"
              class="bqa-metric-dd__opt"
              :class="{ 'is-on': isMetricActive(m.key) }"
              :style="isMetricActive(m.key) ? { '--metric': m.color, '--metric-bg': m.bg } : null"
              role="menuitemcheckbox"
              :aria-checked="isMetricActive(m.key) ? 'true' : 'false'"
              @click.stop="toggleChartMetric(m.key)"
            >
              <span class="bqa-metric-dd__check" aria-hidden="true">
                <span class="bqa-metric-dd__check-box"></span>
              </span>
              <span class="bqa-metric-dd__opt-label">{{ m.label }}</span>
              <span class="bqa-metric-dd__opt-value">
                {{ formatMetricTotal(m.key, stats) }}
                <small v-if="compareEnabled" class="bqa-metric-dd__opt-cmp">{{ formatMetricTotal(m.key, statsCompare) }}</small>
              </span>
            </button>
          </div>
        </div>
      </div>
      <div class="bqa-metric-bar__rating">
        <span class="bqa-metric-bar__rating-label">Оценка ОК (1–5)</span>
        <span class="bqa-metric-bar__rating-value">{{ formatAvgRating(stats.avg_rating) }}</span>
        <span class="bqa-metric-bar__rating-sub">оценено: {{ stats.rated || 0 }}</span>
      </div>
    </section>

    <section v-if="stats" class="bqa-perf">
      <div class="bqa-perf__top">
        <div>
          <h2 class="bqa-perf__title">Динамика</h2>
          <p class="bqa-perf__updated" v-if="lastSync">Обновлено: {{ formatIso(lastSync) }}</p>
        </div>
        <div class="bqa-perf__tools">
          <div class="bqa-seg">
            <button
              v-for="t in chartTypeOptions"
              :key="t.value"
              type="button"
              class="bqa-seg__btn"
              :class="{ 'is-active': chartType === t.value }"
              @click="setChartType(t.value)"
            >{{ t.label }}</button>
          </div>
          <select v-model="chartGranularity" class="bqa-input bqa-select bqa-select--sm" @change="setChartGranularity(chartGranularity)">
            <option value="day">По дням</option>
            <option value="month">По месяцам</option>
            <option value="year">По годам</option>
          </select>
        </div>
      </div>

      <div v-if="compareEnabled" class="bqa-perf__compare-hint">
        <span class="bqa-period-a">Текущий: {{ formatDay(dateFrom) }} — {{ formatDay(dateTo) }}</span>
        <span class="bqa-period-b">Сравнение: {{ formatDay(compareDateFrom) }} — {{ formatDay(compareDateTo) }}</span>
        <span class="bqa-muted">Сплошная — текущий период, пунктир — сравнение. Клик по точке фильтрует список.</span>
      </div>

      <div class="bqa-chart-wrap bqa-chart-wrap--perf">
        <canvas ref="statsChart" height="150"></canvas>
        <p v-if="!chartSeries.length" class="bqa-chart-empty">Нет данных для графика за выбранный период</p>
      </div>
    </section>

    <section v-if="byFailure.length && (outcome === 'all' || outcome === 'fail')" class="bqa-reasons">
      <button type="button" class="bqa-reasons__head" @click="reasonsOpen = !reasonsOpen">
        <span>Почему провал</span>
        <span class="bqa-reasons__count">{{ byFailureTotal }}</span>
        <span class="bqa-reasons__chevron">{{ reasonsOpen ? '▾' : '▸' }}</span>
      </button>
      <div v-show="reasonsOpen" class="bqa-reasons__list">
        <button
          v-for="f in byFailure"
          :key="f.key"
          type="button"
          class="bqa-reason"
          :class="{ 'is-active': failureClass === f.key }"
          @click="filterFail(f.key)"
        >
          <span class="bqa-reason__name">{{ failureLabel(f.key) }}</span>
          <span class="bqa-reason__bar"><i :style="{ width: barWidth(f.cnt) }"></i></span>
          <span class="bqa-reason__cnt">{{ f.cnt }}</span>
        </button>
      </div>
    </section>

    <section class="bqa-list-panel">
      <div class="bqa-list-panel__head">
        <h2>Диалоги</h2>
        <span class="bqa-list-panel__count" v-if="!loading">{{ total }} шт.</span>
        <span class="bqa-list-panel__count" v-else>Загрузка…</span>
      </div>

      <div v-if="!loading && !rows.length" class="bqa-empty">
        Нет диалогов за период. Нажмите «Подтянуть» или смените фильтры.
      </div>

      <div v-else class="bqa-list">
        <article
          v-for="r in rows"
          :key="r.client_id"
          class="bqa-item"
          :class="'bqa-item--' + r.outcome"
          @click="openDetail(r)"
        >
          <div class="bqa-item__main">
            <div class="bqa-item__top">
              <span class="bqa-dialog-id" :title="r.dialog_id ? ('TalkMe #' + r.dialog_id) : 'Номер диалога не найден'">
                #{{ r.dialog_id || '—' }}
              </span>
              <span
                v-if="primaryTagOf(r)"
                :class="['bqa-badge', primaryTagClass(primaryTagOf(r))]"
              >{{ primaryTagOf(r) }}</span>
              <span v-else :class="['bqa-badge', 'bqa-badge--' + r.outcome]">{{ shortOutcome(r) }}</span>
              <span
                v-for="code in clarifyingTagsOf(r).slice(0, 5)"
                :key="'ai-' + code"
                class="bqa-tag-chip bqa-tag-chip--ai"
                :title="'Тег ИИ: ' + code"
                @click.stop="filterByTag(code)"
              >{{ tagLabel(code) }}</span>
              <span
                v-if="r.ai_trigger"
                :class="['bqa-trigger', 'bqa-trigger--' + r.ai_trigger]"
              >{{ triggerLabel(r.ai_trigger) }}</span>
              <span v-if="r.handed_off_to_human" class="bqa-tag-chip bqa-tag-chip--handoff">менеджер</span>
              <span
                v-for="code in (r.manager_tags || []).slice(0, 3)"
                :key="'m-' + code"
                class="bqa-tag-chip bqa-tag-chip--manual"
                :title="'Ручной тег: ' + tagLabel(code)"
              >ручн.: {{ tagLabel(code) }}</span>
              <span class="bqa-item__date">{{ formatDay(r.day_key) }} · {{ formatTs(r.last_message_at) }}</span>
              <span v-if="r.eval_status === 'done'" class="bqa-item__ai" :title="r.ai_evaluated_at ? ('Оценено: ' + formatIso(r.ai_evaluated_at)) : ''">ИИ{{ r.ai_evaluated_at ? ' · ' + formatTsShort(r.ai_evaluated_at) : '' }}</span>
              <span v-else-if="r.outcome === 'pending'" class="bqa-item__ai bqa-item__ai--wait">ждёт ИИ</span>
            </div>
            <p class="bqa-item__summary">
              <template v-if="digestOf(r) && digestOf(r).client_request">
                {{ digestOf(r).client_request }}
                <span v-if="digestOf(r).result" class="bqa-item__result"> · {{ digestOf(r).result }}</span>
              </template>
              <template v-else>{{ r.bot_summary || 'Без выжимки' }}</template>
            </p>
            <p v-if="r.preview_user" class="bqa-item__preview">«{{ r.preview_user }}»</p>
            <div v-if="r.policy_violations && r.policy_violations.length" class="bqa-item__warn">
              ⚠ {{ r.policy_violations.map(v => v.label).join(', ') }}
            </div>
          </div>
          <div class="bqa-item__side" @click.stop>
            <div class="bqa-item__meta">
              <span class="bqa-dialog-id bqa-dialog-id--meta">#{{ r.dialog_id || '—' }}</span>
              <span>{{ r.phone || 'без телефона' }}</span>
              <a
                v-if="r.crm_order_id"
                :href="crmOrderUrl(r)"
                target="_blank"
                rel="noopener"
                class="bqa-order-link"
                @click.stop
              >Заказ {{ crmOrderLabel(r) }} ↗</a>
              <span v-else-if="r.sold">заявка</span>
              <span v-else class="bqa-muted">без заказа</span>
              <span v-if="r.crm_status_label" class="bqa-order-status">{{ r.crm_status_label }}</span>
              <span>{{ r.client_turns }}/{{ r.assistant_turns }} репл.</span>
            </div>
            <div class="bqa-qc-row">
              <span
                class="bqa-rating-chip bqa-rating-chip--ai"
                :title="r.ai_rating_reason || 'Оценка агента'"
              >Агент {{ r.ai_rating != null ? r.ai_rating + '/5' : '—' }}</span>
              <span
                class="bqa-rating-chip"
                :class="{
                  'bqa-rating-chip--agree': r.qc_verdict === 'agree',
                  'bqa-rating-chip--disagree': r.qc_verdict === 'disagree',
                }"
                :title="r.qc_disagree_reason || 'Оценка отдела контроля'"
              >ОКК {{ r.manager_rating != null ? r.manager_rating + '/5' : '—' }}</span>
            </div>
            <div v-if="qcDisagreeFor === r.client_id" class="bqa-qc-disagree" @click.stop>
              <div class="bqa-stars bqa-stars--sm">
                <button
                  v-for="n in 5"
                  :key="'qc-row-' + r.client_id + '-' + n"
                  type="button"
                  class="bqa-star"
                  :class="{ 'is-active': qcDisagreeRating === n }"
                  @click="qcDisagreeRating = n"
                >{{ n }}</button>
              </div>
              <textarea
                v-model="qcDisagreeReason"
                class="bqa-input bqa-qc-reason"
                rows="2"
                placeholder="Почему не согласны с оценкой агента…"
              ></textarea>
              <div class="bqa-item__actions">
                <button
                  type="button"
                  class="bqa-btn bqa-btn--sm bqa-btn--accent"
                  :disabled="annotating || !qcDisagreeRating || !String(qcDisagreeReason || '').trim()"
                  @click="submitQcDisagree(r)"
                >Сохранить</button>
                <button type="button" class="bqa-btn bqa-btn--sm bqa-btn--ghost" @click="cancelQcDisagree">Отмена</button>
              </div>
            </div>
            <div v-else class="bqa-item__actions">
              <button
                type="button"
                class="bqa-btn bqa-btn--sm bqa-btn--accent"
                :disabled="isEvaluating(r.client_id) || aiRunning"
                @click="runAiEvalOne(r)"
              >{{ isEvaluating(r.client_id) ? 'Оценка…' : (r.eval_status === 'done' ? 'Переоценить' : 'Оценить') }}</button>
              <button
                type="button"
                class="bqa-btn bqa-btn--sm bqa-btn--ghost"
                :disabled="annotating || r.ai_rating == null"
                :class="{ 'is-active-soft': r.qc_verdict === 'agree' }"
                @click="agreeWithAiRating(r)"
              >Согласен</button>
              <button
                type="button"
                class="bqa-btn bqa-btn--sm bqa-btn--ghost"
                :disabled="annotating || r.ai_rating == null"
                :class="{ 'is-active-soft': r.qc_verdict === 'disagree' }"
                @click="startQcDisagree(r)"
              >Не согласен</button>
              <a
                v-if="r.crm_order_id"
                :href="crmOrderUrl(r)"
                target="_blank"
                rel="noopener"
                class="bqa-btn bqa-btn--sm bqa-btn--crm"
              >RetailCRM</a>
              <a
                v-if="r.talkme_url"
                :href="r.talkme_url"
                target="_blank"
                rel="noopener"
                class="bqa-btn bqa-btn--sm bqa-btn--link"
              >TalkMe</a>
            </div>
          </div>
        </article>
      </div>

      <div v-if="total > 0" class="bqa-pager">
        <button type="button" class="bqa-btn bqa-btn--ghost" :disabled="page <= 1" @click="goPage(page - 1)">‹ Назад</button>
        <span>Стр. {{ page }} / {{ totalPages }}</span>
        <button type="button" class="bqa-btn bqa-btn--ghost" :disabled="page >= totalPages" @click="goPage(page + 1)">Вперёд ›</button>
        <select v-model.number="perPage" class="bqa-input bqa-select bqa-select--sm" @change="reload">
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </div>
    </section>

    <div v-if="detail" class="bqa-modal" @click.self="closeDetail">
      <div class="bqa-modal__box">
        <div class="bqa-modal__head">
          <div>
            <h2>
              Диалог
              #{{ (detail.qa && detail.qa.dialog_id) || (detail.meta && detail.meta.dialog_id) || '—' }}
            </h2>
            <p v-if="detail.qa && digestOf(detail.qa)" class="bqa-modal__summary">
              {{ digestOf(detail.qa).client_request || detail.qa.bot_summary }}
            </p>
            <p v-else-if="detail.qa" class="bqa-modal__summary">{{ detail.qa.bot_summary }}</p>
          </div>
          <button type="button" class="bqa-modal__close" @click="closeDetail">×</button>
        </div>

        <div v-if="detail.qa && digestOf(detail.qa)" class="bqa-digest">
          <div v-if="digestOf(detail.qa).summary" class="bqa-digest__row">
            <span class="bqa-digest__label">Выжимка</span>
            <p>{{ digestOf(detail.qa).summary }}</p>
          </div>
          <div v-if="digestOf(detail.qa).result" class="bqa-digest__row">
            <span class="bqa-digest__label">Итог</span>
            <p>{{ digestOf(detail.qa).result }}</p>
          </div>
          <div v-if="(digestOf(detail.qa).confirmed_facts || []).length" class="bqa-digest__row">
            <span class="bqa-digest__label">Факты</span>
            <ul>
              <li v-for="(f, i) in digestOf(detail.qa).confirmed_facts" :key="'cf-' + i">{{ f }}</li>
            </ul>
          </div>
          <div v-if="(digestOf(detail.qa).unresolved_issues || []).length" class="bqa-digest__row">
            <span class="bqa-digest__label">Не решено</span>
            <ul>
              <li v-for="(f, i) in digestOf(detail.qa).unresolved_issues" :key="'ui-' + i">{{ f }}</li>
            </ul>
          </div>
          <div v-if="(digestOf(detail.qa).service_quality_issues || []).length" class="bqa-digest__row bqa-digest__row--warn">
            <span class="bqa-digest__label">Качество сервиса</span>
            <ul>
              <li v-for="(f, i) in digestOf(detail.qa).service_quality_issues" :key="'sq-' + i">{{ f }}</li>
            </ul>
          </div>
        </div>

        <div v-if="detail.qa" class="bqa-modal__meta">
          <span :class="['bqa-badge', 'bqa-badge--' + detail.qa.outcome]">{{ detail.qa.outcome_label || detail.qa.outcome }}</span>
          <span
            v-if="primaryTagOf(detail.qa)"
            :class="['bqa-badge', primaryTagClass(primaryTagOf(detail.qa))]"
          >{{ primaryTagOf(detail.qa) }}</span>
          <span
            v-for="code in clarifyingTagsOf(detail.qa)"
            :key="'dtag-' + code"
            class="bqa-tag-chip bqa-tag-chip--ai"
            @click.stop="filterByTag(code)"
          >{{ tagLabel(code) }}</span>
          <span
            v-if="detail.qa.ai_trigger"
            :class="['bqa-trigger', 'bqa-trigger--' + detail.qa.ai_trigger]"
          >{{ triggerLabel(detail.qa.ai_trigger) }}</span>
          <span v-if="detail.qa.phone">{{ detail.qa.phone }}</span>
          <a
            v-if="detail.qa.crm_order_id"
            :href="crmOrderUrl(detail.qa)"
            target="_blank"
            rel="noopener"
            class="bqa-order-link"
          >Заказ {{ crmOrderLabel(detail.qa) }} ↗</a>
          <span v-if="detail.qa.crm_status_label" class="bqa-order-status">{{ detail.qa.crm_status_label }}</span>
          <span v-if="detail.qa.active_agent">Агент: {{ detail.qa.active_agent }}</span>
          <a v-if="detail.qa.talkme_url" :href="detail.qa.talkme_url" target="_blank" rel="noopener">TalkMe ↗</a>
        </div>

        <div class="bqa-modal__toolbar">
          <button
            type="button"
            class="bqa-btn bqa-btn--accent"
            :disabled="!detail.qa || isEvaluating(detail.qa.client_id) || aiRunning"
            @click="runAiEvalOne(detail.qa)"
          >{{ detail.qa && isEvaluating(detail.qa.client_id) ? 'Оценка…' : (detail.qa && detail.qa.eval_status === 'done' ? 'Переоценить ИИ' : 'Оценить ИИ') }}</button>
          <button type="button" class="bqa-btn bqa-btn--primary" @click="copyDetailChat">Скопировать диалог</button>
          <button type="button" class="bqa-btn bqa-btn--ghost" @click="copyDetailSummary">Скопировать выжимку</button>
          <span v-if="copyHint" class="bqa-copy-hint">{{ copyHint }}</span>
        </div>

        <div v-if="detail.qa" class="bqa-annotate" @click.stop>
          <div class="bqa-annotate__block">
            <span class="bqa-annotate__label">Оценка агента (1–5)</span>
            <div class="bqa-qc-row">
              <span class="bqa-rating-chip bqa-rating-chip--ai">
                {{ detail.qa.ai_rating != null ? detail.qa.ai_rating + '/5' : 'ещё нет' }}
              </span>
              <span v-if="detail.qa.ai_rating_reason" class="bqa-muted">{{ detail.qa.ai_rating_reason }}</span>
            </div>
          </div>
          <div class="bqa-annotate__block">
            <span class="bqa-annotate__label">Отдел контроля</span>
            <div class="bqa-item__actions" style="margin-bottom: 8px;">
              <button
                type="button"
                class="bqa-btn bqa-btn--sm bqa-btn--ghost"
                :disabled="annotating || detail.qa.ai_rating == null"
                :class="{ 'is-active-soft': detail.qa.qc_verdict === 'agree' }"
                @click="agreeWithAiRating(detail.qa)"
              >Согласен с оценкой</button>
              <button
                type="button"
                class="bqa-btn bqa-btn--sm bqa-btn--ghost"
                :disabled="annotating || detail.qa.ai_rating == null"
                :class="{ 'is-active-soft': detail.qa.qc_verdict === 'disagree' || qcDisagreeFor === detail.qa.client_id }"
                @click="startQcDisagree(detail.qa)"
              >Не согласен</button>
            </div>
            <div v-if="qcDisagreeFor === detail.qa.client_id" class="bqa-qc-disagree">
              <span class="bqa-annotate__label">Своя оценка (1–5) и причина</span>
              <div class="bqa-stars">
                <button
                  v-for="n in 5"
                  :key="'star-qc-' + n"
                  type="button"
                  class="bqa-star"
                  :class="{ 'is-active': qcDisagreeRating === n }"
                  @click="qcDisagreeRating = n"
                >{{ n }}</button>
              </div>
              <textarea
                v-model="qcDisagreeReason"
                class="bqa-input bqa-qc-reason"
                rows="2"
                placeholder="Почему не согласны…"
              ></textarea>
              <div class="bqa-item__actions">
                <button
                  type="button"
                  class="bqa-btn bqa-btn--sm bqa-btn--accent"
                  :disabled="annotating || !qcDisagreeRating || !String(qcDisagreeReason || '').trim()"
                  @click="submitQcDisagree(detail.qa)"
                >Сохранить</button>
                <button type="button" class="bqa-btn bqa-btn--sm bqa-btn--ghost" @click="cancelQcDisagree">Отмена</button>
              </div>
            </div>
            <template v-else>
              <span class="bqa-annotate__label">Оценка ОК (1–5)</span>
              <div class="bqa-stars">
                <button
                  v-for="n in 5"
                  :key="'star-' + n"
                  type="button"
                  class="bqa-star"
                  :class="{ 'is-active': detail.qa.manager_rating === n }"
                  :disabled="annotating"
                  @click="setDetailRating(n)"
                >{{ n }}</button>
                <button
                  type="button"
                  class="bqa-btn bqa-btn--sm bqa-btn--ghost"
                  :disabled="annotating || detail.qa.manager_rating == null"
                  @click="setDetailRating(null)"
                >Сбросить</button>
              </div>
              <p v-if="detail.qa.qc_verdict === 'disagree' && detail.qa.qc_disagree_reason" class="bqa-eval-hint">
                Несогласие: {{ detail.qa.qc_disagree_reason }}
              </p>
            </template>
          </div>
          <div class="bqa-annotate__block">
            <span class="bqa-annotate__label">Ручные теги</span>
            <p class="bqa-eval-hint">Ставятся менеджером вручную. Не путать с тегами ИИ ниже.</p>
            <div class="bqa-tag-list">
              <button
                v-for="t in manualTagPresets"
                :key="'manual-' + t.code"
                type="button"
                class="bqa-tag-toggle bqa-tag-toggle--manual"
                :class="{ 'is-active': hasDetailTag(t.code) }"
                :disabled="annotating"
                @click="toggleDetailTag(t.code)"
              >{{ t.label }}</button>
            </div>
            <div v-if="detail.qa.ai_tags && detail.qa.ai_tags.length" class="bqa-ai-tags-view">
              <span class="bqa-annotate__label">Теги от ИИ</span>
              <div class="bqa-tag-list">
                <span
                  v-for="code in detail.qa.ai_tags"
                  :key="'ai-view-' + code"
                  class="bqa-tag-chip bqa-tag-chip--ai"
                  @click.stop="filterByTag(code)"
                >{{ tagLabel(code) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="bqa-chat">
          <div
            v-for="(m, i) in detail.history"
            :key="i"
            :class="['bqa-bubble', bubbleClass(m)]"
          >
            <div class="bqa-bubble__meta">
              <strong>{{ bubbleAuthor(m) }}</strong>
              <span v-if="m.agent && !isManagerMessage(m)">{{ m.agent }}</span>
              <span>{{ formatTs(m.timestamp) }}</span>
            </div>
            <div class="bqa-bubble__text">{{ m.text }}</div>
          </div>
          <p v-if="!detail.history.length" class="bqa-empty">История пуста</p>
        </div>
      </div>
    </div>

    <div v-if="dateModalOpen" class="bqa-date-backdrop" @click.self="closeDateModal">
      <div class="bqa-date-modal" role="dialog" aria-label="Диапазон дат">
        <h3 class="bqa-date-modal__title">Диапазон дат</h3>
        <div class="bqa-date-tabs">
          <button type="button" class="bqa-date-tab" :class="{ 'is-active': dateModalTab === 'filter' }" @click="dateModalTab = 'filter'">Фильтр</button>
          <button type="button" class="bqa-date-tab" :class="{ 'is-active': dateModalTab === 'compare' }" @click="dateModalTab = 'compare'">Сравнить</button>
        </div>

        <div v-if="dateModalTab === 'filter'" class="bqa-date-body">
          <label v-for="p in dateFilterOptions" :key="'f-' + p.value" class="bqa-date-radio">
            <input v-model="dateModalFilterPreset" type="radio" :value="p.value" />
            <span>{{ p.label }}</span>
          </label>
          <div v-if="dateModalFilterPreset === 'custom'" class="bqa-date-custom">
            <label class="bqa-outlined">
              <span>Дата начала</span>
              <input v-model="dateModalFrom" type="date" />
              <small>ГГГГ-ММ-ДД</small>
            </label>
            <span class="bqa-dates__sep">—</span>
            <label class="bqa-outlined">
              <span>Дата окончания</span>
              <input v-model="dateModalTo" type="date" />
              <small>ГГГГ-ММ-ДД</small>
            </label>
          </div>
        </div>

        <div v-else class="bqa-date-body">
          <label v-for="p in dateCompareOptions" :key="'c-' + p.value" class="bqa-date-radio">
            <input v-model="dateModalComparePreset" type="radio" :value="p.value" />
            <span>{{ p.label }}</span>
          </label>
          <div v-if="dateModalComparePreset === 'custom'" class="bqa-date-custom">
            <label class="bqa-outlined">
              <span>Дата начала (B)</span>
              <input v-model="dateModalCompareFrom" type="date" />
              <small>ГГГГ-ММ-ДД</small>
            </label>
            <span class="bqa-dates__sep">—</span>
            <label class="bqa-outlined">
              <span>Дата окончания (B)</span>
              <input v-model="dateModalCompareTo" type="date" />
              <small>ГГГГ-ММ-ДД</small>
            </label>
          </div>
          <button type="button" class="bqa-date-clear" @click="disableCompareFromModal">Отключить сравнение</button>
        </div>

        <div class="bqa-date-footer">
          <button type="button" class="bqa-date-cancel" @click="closeDateModal">Отмена</button>
          <button type="button" class="bqa-date-apply" :disabled="!dateModalCanApply" @click="applyDateModal">Применить</button>
        </div>
      </div>
    </div>

    <div v-if="toast" class="bqa-toast">{{ toast }}</div>

    <div v-if="evalConfigOpen" class="bqa-modal" @click.self="closeEvalConfig">
      <div class="bqa-modal__box bqa-modal__box--wide">
        <div class="bqa-modal__head">
          <div>
            <h2>Промпт оценщика ИИ</h2>
            <p class="bqa-modal__summary">
              Два этапа: 1) выжимка фактов в JSON; 2) теги по выжимке (без исходного диалога).
            </p>
          </div>
          <button type="button" class="bqa-modal__close" @click="closeEvalConfig">×</button>
        </div>

        <div class="bqa-eval-config">
          <label class="bqa-annotate__label">1. Промпт выжимки</label>
          <textarea
            v-model="evalPromptDraft"
            class="bqa-prompt"
            rows="12"
            spellcheck="false"
          ></textarea>

          <label class="bqa-annotate__label">2. Промпт тегов</label>
          <textarea
            v-model="evalTagsPromptDraft"
            class="bqa-prompt"
            rows="12"
            spellcheck="false"
          ></textarea>

          <div class="bqa-annotate__block">
            <span class="bqa-annotate__label">Справочник тегов ИИ</span>
            <p class="bqa-eval-hint">
              Таксономия для второго агента и фильтра «Теги ИИ».
              Код и название обычно совпадают (русский лейбл).
            </p>
            <div class="bqa-ai-tags-editor">
              <div
                v-for="(t, idx) in evalTagsDraft"
                :key="'etag-' + idx"
                class="bqa-ai-tag-row"
              >
                <input v-model="t.code" class="bqa-input" placeholder="тег" />
                <input v-model="t.label" class="bqa-input" placeholder="Название" />
                <input v-model="t.hint" class="bqa-input bqa-input--grow" placeholder="Подсказка (необяз.)" />
                <button type="button" class="bqa-btn bqa-btn--sm bqa-btn--ghost" @click="removeEvalTag(idx)">Удалить</button>
              </div>
            </div>
            <div class="bqa-ai-tag-add">
              <input v-model="newTagCode" class="bqa-input" placeholder="новый тег" @keyup.enter="addEvalTag" />
              <input v-model="newTagLabel" class="bqa-input" placeholder="Название" @keyup.enter="addEvalTag" />
              <input v-model="newTagHint" class="bqa-input bqa-input--grow" placeholder="Подсказка" @keyup.enter="addEvalTag" />
              <button type="button" class="bqa-btn bqa-btn--sm bqa-btn--primary" @click="addEvalTag">Добавить</button>
            </div>
          </div>

          <div class="bqa-annotate__block">
            <span class="bqa-annotate__label">Ручные теги (менеджер)</span>
            <p class="bqa-eval-hint">
              Эти теги ставятся руками в карточке диалога. Отдельно от тегов ИИ.
            </p>
            <div class="bqa-ai-tags-editor">
              <div
                v-for="(t, idx) in evalManualTagsDraft"
                :key="'mtag-' + idx"
                class="bqa-ai-tag-row"
              >
                <input v-model="t.code" class="bqa-input" placeholder="code" />
                <input v-model="t.label" class="bqa-input" placeholder="Название" />
                <input v-model="t.hint" class="bqa-input bqa-input--grow" placeholder="Подсказка (необяз.)" />
                <button type="button" class="bqa-btn bqa-btn--sm bqa-btn--ghost" @click="removeManualTag(idx)">Удалить</button>
              </div>
            </div>
            <div class="bqa-ai-tag-add">
              <input v-model="newManualTagCode" class="bqa-input" placeholder="новый_код" @keyup.enter="addManualTag" />
              <input v-model="newManualTagLabel" class="bqa-input" placeholder="Название" @keyup.enter="addManualTag" />
              <input v-model="newManualTagHint" class="bqa-input bqa-input--grow" placeholder="Подсказка" @keyup.enter="addManualTag" />
              <button type="button" class="bqa-btn bqa-btn--sm bqa-btn--primary" @click="addManualTag">Добавить</button>
            </div>
          </div>

          <div class="bqa-eval-actions">
            <button type="button" class="bqa-btn bqa-btn--primary" :disabled="evalConfigSaving" @click="saveEvalConfig">
              {{ evalConfigSaving ? 'Сохранение…' : 'Сохранить' }}
            </button>
            <button type="button" class="bqa-btn bqa-btn--ghost" :disabled="evalConfigSaving" @click="resetEvalPrompt">
              Сбросить выжимку
            </button>
            <button type="button" class="bqa-btn bqa-btn--ghost" :disabled="evalConfigSaving" @click="resetEvalTagsPrompt">
              Сбросить теги-промпт
            </button>
            <button type="button" class="bqa-btn bqa-btn--ghost" :disabled="evalConfigSaving" @click="resetEvalTags">
              Сбросить теги ИИ
            </button>
            <button type="button" class="bqa-btn bqa-btn--ghost" :disabled="evalConfigSaving" @click="resetManualTags">
              Сбросить ручные
            </button>
            <span v-if="evalConfigMeta" class="bqa-meta">{{ evalConfigMeta }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ORDERS_API_BASE } from '@/config/api';
import Chart from 'chart.js/dist/Chart.js';

const API_BASE = 'https://manager.1akb.by';

function todayYmd() {
  const d = new Date();
  const msk = new Date(d.getTime() + 3 * 3600 * 1000);
  return msk.toISOString().slice(0, 10);
}

function daysAgoYmd(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const msk = new Date(d.getTime() + 3 * 3600 * 1000);
  return msk.toISOString().slice(0, 10);
}

function monthsAgoYmd(n) {
  const d = new Date();
  const msk = new Date(d.getTime() + 3 * 3600 * 1000);
  msk.setUTCMonth(msk.getUTCMonth() - n);
  return msk.toISOString().slice(0, 10);
}

function yearsAgoYmd(n) {
  const d = new Date();
  const msk = new Date(d.getTime() + 3 * 3600 * 1000);
  msk.setUTCFullYear(msk.getUTCFullYear() - n);
  return msk.toISOString().slice(0, 10);
}

export default {
  name: 'BotDialogAnalytics',
  data() {
    return {
      dateFrom: daysAgoYmd(6),
      dateTo: todayYmd(),
      periodPreset: '7d',
      chartGranularity: 'day',
      chartType: 'line',
      chartMetric: 'total',
      activeChartMetrics: ['total', 'tag_successful'],
      metricDropdownOpen: false,
      compareEnabled: false,
      compareMode: 'prev',
      compareDateFrom: '',
      compareDateTo: '',
      byDayCompare: [],
      statsCompare: null,
      dateModalOpen: false,
      dateModalTab: 'filter',
      dateModalFilterPreset: '7d',
      dateModalComparePreset: 'prev_equal',
      dateModalFrom: '',
      dateModalTo: '',
      dateModalCompareFrom: '',
      dateModalCompareTo: '',
      outcome: 'all',
      primaryTagFilter: '',
      trigger: 'all',
      failureClass: '',
      onlySold: false,
      onlyOrder: false,
      onlyHasPhone: false,
      ratingFilter: '',
      qcDisagreeFor: null,
      qcDisagreeRating: null,
      qcDisagreeReason: '',
      tagFilter: '',
      tagPresets: [
        { code: 'успешный', label: 'успешный' },
        { code: 'неуспешный', label: 'неуспешный' },
        { code: 'недостаточно информации', label: 'недостаточно информации' },
      ],
      manualTagPresets: [
        { code: 'spam', label: 'Спамил' },
        { code: 'phone_nag', label: 'Просил телефон много раз' },
        { code: 'wrong_fit', label: 'Неправильный подбор' },
        { code: 'bot_detected', label: 'Клиент понял что бот' },
        { code: 'rude', label: 'Грубость / негатив' },
        { code: 'good_example', label: 'Хороший пример' },
        { code: 'need_recheck', label: 'Нужна перепроверка' },
        { code: 'training', label: 'В обучающую выборку' },
      ],
      q: '',
      page: 1,
      perPage: 25,
      total: 0,
      rows: [],
      stats: null,
      byDay: [],
      byFailure: [],
      byTrigger: [],
      reasonsOpen: false,
      lastSync: null,
      loading: false,
      syncing: false,
      aiRunning: false,
      evaluatingClientId: null,
      annotating: false,
      evalConfigOpen: false,
      evalConfigSaving: false,
      evalPromptDraft: '',
      evalTagsPromptDraft: '',
      evalTagsDraft: [],
      evalManualTagsDraft: [],
      evalDefaultPrompt: '',
      evalDefaultTagsPrompt: '',
      evalDefaultTags: [],
      evalDefaultManualTags: [],
      evalConfigMeta: '',
      newTagCode: '',
      newTagLabel: '',
      newTagHint: '',
      newManualTagCode: '',
      newManualTagLabel: '',
      newManualTagHint: '',
      detail: null,
      maxFailCnt: 1,
      copyHint: '',
      toast: '',
      toastTimer: null,
      copyHintTimer: null,
      statsChart: null,
      statusNameMap: {},
      outcomeOptions: [
        { value: 'all', label: 'Все' },
        { value: 'pending', label: 'Ждут ИИ' },
        { value: 'success', label: 'Заказ CRM (старый)' },
        { value: 'consult', label: 'Consult (старый)' },
        { value: 'fail', label: 'Fail (старый)' },
        { value: 'exclude', label: 'Exclude (старый)' },
      ],
      primaryTagOptions: [
        { value: '', label: 'Все' },
        { value: 'успешный', label: 'Успешный' },
        { value: 'неуспешный', label: 'Неуспешный' },
        { value: 'недостаточно информации', label: 'Недостаточно информации' },
      ],
      triggerOptions: [
        { value: 'all', label: 'Все' },
        { value: 'purchase', label: 'Покупка' },
        { value: 'warranty', label: 'Гарантия' },
        { value: 'non_target', label: 'Нецелевой' },
        { value: 'none', label: 'Без триггера' },
      ],
      periodPresets: [
        { value: '7d', label: '7 дней' },
        { value: '28d', label: '28 дней' },
        { value: '90d', label: '3 месяца' },
        { value: '12m', label: '12 месяцев' },
      ],
      dateFilterOptions: [
        { value: '7d', label: 'За последние 7 дней' },
        { value: '28d', label: 'За последние 28 дней' },
        { value: '90d', label: 'За последние 3 месяца' },
        { value: '12m', label: 'За последние 12 месяцев' },
        { value: 'custom', label: 'Свой вариант' },
      ],
      dateCompareOptions: [
        { value: 'prev_equal', label: 'Сравнить с предыдущим таким же периодом' },
        { value: 'prev_year', label: 'Сравнить с тем же периодом год назад' },
        { value: '7d_prev', label: 'Сравнить последние 7 дней с предыдущим периодом' },
        { value: '28d_prev', label: 'Сравнить последние 28 дней с предыдущим периодом' },
        { value: '90d_prev', label: 'Сравнить последние 3 месяца с предыдущим периодом' },
        { value: 'custom', label: 'Свой вариант' },
      ],
      chartMetricDefs: [
        { key: 'tag_successful', label: 'Успешный', color: '#0f7a45', bg: '#e6f4ea', column: 'status' },
        { key: 'tag_failed', label: 'Неуспешный', color: '#d93025', bg: '#fce8e6', column: 'status' },
        { key: 'tag_insufficient', label: 'Недостаточно информации', color: '#7b61ff', bg: '#f0ebff', column: 'status' },
        { key: 'pending', label: 'Ждут ИИ', color: '#5f6368', bg: '#f1f3f4', column: 'orders' },
        { key: 'with_order', label: 'Заказы CRM', color: '#188038', bg: '#e6f4ea', column: 'orders' },
        { key: 'with_phone', label: 'С телефоном', color: '#e37400', bg: '#fef7e0', column: 'orders' },
        { key: 'total', label: 'Всего', color: '#1a73e8', bg: '#e8f0fe', column: 'orders' },
        { key: 'trigger_purchase', label: 'Покупка', color: '#059669', bg: '#d1fae5', column: 'topic' },
        { key: 'trigger_warranty', label: 'Гарантия', color: '#ea580c', bg: '#ffedd5', column: 'topic' },
        { key: 'trigger_non_target', label: 'Нецелевой', color: '#64748b', bg: '#f1f5f9', column: 'topic' },
      ],
      chartMetricColumnDefs: [
        { id: 'status', title: 'Статус диалога' },
        { id: 'orders', title: 'Количество заказов' },
        { id: 'topic', title: 'Тема диалога' },
      ],
      granularityOptions: [
        { value: 'day', label: 'Дни' },
        { value: 'month', label: 'Месяцы' },
        { value: 'year', label: 'Годы' },
      ],
      chartTypeOptions: [
        { value: 'line', label: 'Линии' },
        { value: 'bar', label: 'Столбики' },
      ],
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.perPage));
    },
    byFailureTotal() {
      return (this.byFailure || []).reduce((sum, f) => sum + (Number(f.cnt) || 0), 0);
    },
    chartGranularityLabel() {
      return ({ day: 'по дням', month: 'по месяцам', year: 'по годам' })[this.chartGranularity] || '';
    },
    chartSubtitle() {
      const type = this.chartType === 'line' ? 'линии' : 'столбики';
      const cmp = this.compareEnabled ? ' · сравнение периодов' : '';
      return type + ' · ' + this.chartGranularityLabel + cmp;
    },
    chartSeries() {
      return this.rollupByGranularity(
        this.byDay || [],
        this.chartGranularity,
        this.dateFrom,
        this.dateTo
      );
    },
    chartSeriesCompare() {
      if (!this.compareEnabled) return [];
      return this.rollupByGranularity(
        this.byDayCompare || [],
        this.chartGranularity,
        this.compareDateFrom,
        this.compareDateTo
      );
    },
    dateModalCanApply() {
      if (this.dateModalTab === 'filter') {
        if (this.dateModalFilterPreset === 'custom') {
          return Boolean(this.dateModalFrom && this.dateModalTo && this.dateModalFrom <= this.dateModalTo);
        }
        return true;
      }
      if (this.dateModalComparePreset === 'custom') {
        return Boolean(this.dateModalCompareFrom && this.dateModalCompareTo && this.dateModalCompareFrom <= this.dateModalCompareTo);
      }
      return true;
    },
    clarifyingTagPresets() {
      const primary = new Set(['успешный', 'неуспешный', 'недостаточно информации']);
      return (this.tagPresets || []).filter((t) => t && t.code && !primary.has(t.code));
    },
    effectiveTagFilter() {
      // основной тег ИИ приоритетнее уточняющего, если оба выбраны — ищем основной
      return this.primaryTagFilter || this.tagFilter || '';
    },
    chartMetricColumns() {
      const defs = this.chartMetricDefs || [];
      return (this.chartMetricColumnDefs || []).map((col) => ({
        ...col,
        items: defs.filter((m) => m.column === col.id),
      }));
    },
    metricDropdownSummary() {
      const active = this.activeChartMetrics || [];
      if (!active.length) return 'не выбрано';
      const labels = active.map((key) => {
        const def = (this.chartMetricDefs || []).find((m) => m.key === key);
        return def ? def.label : key;
      });
      if (labels.length <= 2) return labels.join(', ');
      return labels.slice(0, 2).join(', ') + ' +' + (labels.length - 2);
    },
  },
  mounted() {
    this._onMetricDdDocClick = (e) => {
      if (!this.metricDropdownOpen) return;
      const root = this.$refs.metricDropdown;
      if (root && !root.contains(e.target)) this.metricDropdownOpen = false;
    };
    document.addEventListener('click', this._onMetricDdDocClick);
    this.fetchStatusNames().finally(() => this.reload());
  },
  beforeDestroy() {
    if (this._onMetricDdDocClick) {
      document.removeEventListener('click', this._onMetricDdDocClick);
    }
    if (this.toastTimer) clearTimeout(this.toastTimer);
    if (this.copyHintTimer) clearTimeout(this.copyHintTimer);
    this.destroyChart();
  },
  methods: {
    pct(a, b) {
      if (!b) return '—';
      return Math.round((100 * (a || 0)) / b) + '%';
    },
    formatAvgRating(v) {
      if (v == null || v === '' || Number.isNaN(Number(v))) return '—';
      return Number(v).toFixed(1);
    },
    tagLabel(code) {
      const found =
        (this.tagPresets || []).find((t) => t.code === code) ||
        (this.manualTagPresets || []).find((t) => t.code === code);
      return (found && found.label) || code;
    },
    hasDetailTag(code) {
      const tags = (this.detail && this.detail.qa && this.detail.qa.manager_tags) || [];
      return tags.indexOf(code) !== -1;
    },
    barWidth(cnt) {
      const max = this.maxFailCnt || 1;
      return Math.max(6, Math.round((100 * cnt) / max)) + '%';
    },
    failureLabel(key) {
      return (
        {
          dropout_after_first_reply: 'Ушёл после 1 реплики',
          clarify_no_prices: 'Уточнения без цен',
          prices_shown_ghosted: 'Цены показаны, пропал',
          callback_form_only: 'Только форма',
          empty: 'Пустой',
        }[key] || key
      );
    },
    shortOutcome(r) {
      const primary = this.primaryTagOf(r);
      if (primary) return primary;
      const map = {
        success: 'Продажа',
        consult: 'Успешный*',
        fail: 'Неуспешный*',
        pending: 'Ждёт',
        exclude: 'Недостаточно информации*',
      };
      return map[r.outcome] || r.outcome_label || r.outcome;
    },
    primaryTagOf(row) {
      const tags = (row && row.ai_tags) || [];
      const primary = ['успешный', 'неуспешный', 'недостаточно информации'];
      for (let i = 0; i < tags.length; i++) {
        if (primary.indexOf(tags[i]) !== -1) return tags[i];
      }
      return null;
    },
    clarifyingTagsOf(row) {
      const tags = (row && row.ai_tags) || [];
      const primary = new Set(['успешный', 'неуспешный', 'недостаточно информации']);
      return tags.filter((t) => !primary.has(t));
    },
    primaryTagClass(tag) {
      if (tag === 'успешный') return 'bqa-badge--success';
      if (tag === 'неуспешный') return 'bqa-badge--fail';
      if (tag === 'недостаточно информации') return 'bqa-badge--consult';
      return 'bqa-badge--pending';
    },
    setPrimaryTag(value) {
      const next = this.primaryTagFilter === value ? '' : value;
      this.primaryTagFilter = next;
      this.outcome = 'all';
      if (next) this.tagFilter = '';
      this.page = 1;
      this.reload();
    },
    clearPrimaryAndOutcome() {
      this.primaryTagFilter = '';
      this.tagFilter = '';
      this.outcome = 'all';
      this.page = 1;
      this.reload();
    },
    filterByTag(code) {
      const primary = new Set(['успешный', 'неуспешный', 'недостаточно информации']);
      if (primary.has(code)) {
        this.setPrimaryTag(code);
        return;
      }
      this.primaryTagFilter = '';
      this.tagFilter = code;
      this.outcome = 'all';
      this.page = 1;
      this.reload();
    },
    onTagFilterChange() {
      if (this.tagFilter) this.primaryTagFilter = '';
      this.page = 1;
      this.reload();
    },
    triggerLabel(code) {
      return (
        {
          purchase: 'Покупка',
          warranty: 'Гарантия',
          non_target: 'Нецелевой',
        }[code] || code || '—'
      );
    },
    crmOrderUrl(row) {
      const id = row && (row.crm_order_slug || row.crm_order_id);
      if (!id) return '#';
      return `https://1akb.retailcrm.ru/orders/${encodeURIComponent(id)}/edit`;
    },
    crmOrderLabel(row) {
      if (!row || !row.crm_order_id) return '';
      const num = row.crm_order_number || row.crm_order_id;
      return `№${num}`;
    },
    statusLabel(code) {
      if (!code) return '';
      return this.statusNameMap[code] || code;
    },
    async fetchStatusNames() {
      try {
        const res = await fetch(ORDERS_API_BASE + '/statuses.php');
        const data = await res.json();
        const map = {};
        (data.statuses || []).forEach((s) => {
          if (s && s.code) map[s.code] = s.name || s.code;
        });
        this.statusNameMap = map;
      } catch (e) {
        console.error('statuses load failed', e);
      }
    },
    async enrichCrmOrders(rows) {
      const list = Array.isArray(rows) ? rows : [];
      const ids = [...new Set(
        list
          .map((r) => String(r.crm_order_id || '').trim())
          .filter((id) => /^\d+$/.test(id))
      )];
      if (!ids.length) return;

      try {
        const qs = new URLSearchParams();
        ids.forEach((id) => qs.append('ids[]', id));
        const res = await fetch(`${ORDERS_API_BASE}/order_statuses.php?${qs}`);
        const data = await res.json();
        if (!data.success || !Array.isArray(data.orders)) return;

        const byId = {};
        data.orders.forEach((o) => {
          if (o && o.id != null) byId[String(o.id)] = o;
        });

        list.forEach((row) => {
          const info = byId[String(row.crm_order_id)];
          if (!info) return;
          this.$set(row, 'crm_order_number', info.number || row.crm_order_id);
          this.$set(row, 'crm_order_slug', String(info.id));
          this.$set(row, 'crm_status', info.status || '');
          this.$set(row, 'crm_status_label', this.statusLabel(info.status));
        });
      } catch (e) {
        console.error('crm enrich failed', e);
      }
    },
    formatDay(ymd) {
      if (!ymd) return '—';
      const [y, m, d] = ymd.split('-');
      return `${d}.${m}.${y}`;
    },
    formatTs(ts) {
      if (!ts) return '';
      const n = Number(ts);
      const ms = n > 1e12 ? n : n * 1000;
      const d = new Date(ms);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
      });
    },
    formatIso(iso) {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleString('ru-RU');
      } catch (_) {
        return iso;
      }
    },
    formatTsShort(isoOrTs) {
      if (!isoOrTs) return '';
      try {
        let d;
        if (typeof isoOrTs === 'string' && isoOrTs.includes('T')) {
          d = new Date(isoOrTs);
        } else {
          const n = Number(isoOrTs);
          const ms = n > 1e12 ? n : n * 1000;
          d = new Date(ms);
        }
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
      } catch (_) {
        return '';
      }
    },
    showToast(msg) {
      this.toast = msg;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toast = '';
      }, 2200);
    },
    async copyText(text, okMsg) {
      const value = String(text || '').trim();
      if (!value) {
        this.showToast('Нечего копировать');
        return;
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          const ta = document.createElement('textarea');
          ta.value = value;
          ta.setAttribute('readonly', '');
          ta.style.position = 'fixed';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        this.showToast(okMsg || 'Скопировано');
        this.copyHint = 'Скопировано';
        if (this.copyHintTimer) clearTimeout(this.copyHintTimer);
        this.copyHintTimer = setTimeout(() => {
          this.copyHint = '';
        }, 1800);
      } catch (e) {
        this.showToast('Не удалось скопировать');
      }
    },
    buildChatText(detail) {
      const qa = (detail && detail.qa) || {};
      const lines = [];
      lines.push(`Диалог бота${qa.dialog_id ? ' #' + qa.dialog_id : ''}`);
      if (qa.outcome_label || qa.outcome) lines.push(`Исход: ${qa.outcome_label || qa.outcome}`);
      if (qa.ai_trigger) lines.push(`Триггер: ${this.triggerLabel(qa.ai_trigger)}`);
      if (qa.phone) lines.push(`Телефон: ${qa.phone}`);
      if (qa.crm_order_id) lines.push(`Заказ: ${this.crmOrderLabel(qa)}`);
      if (qa.crm_status_label) lines.push(`Статус CRM: ${qa.crm_status_label}`);
      const digestCopy = this.formatDigestCopy(qa);
      if (digestCopy) lines.push(digestCopy);
      else if (qa.bot_summary) lines.push(`Выжимка: ${qa.bot_summary}`);
      if (qa.crm_order_id) lines.push(`RetailCRM: ${this.crmOrderUrl(qa)}`);
      if (qa.talkme_url) lines.push(`TalkMe: ${qa.talkme_url}`);
      lines.push('');
      (detail.history || []).forEach((m) => {
        const who = m.role === 'user' ? 'Клиент' : 'Бот';
        const time = this.formatTs(m.timestamp);
        lines.push(`[${who}${time ? ' · ' + time : ''}]`);
        lines.push(m.text || '');
        lines.push('');
      });
      return lines.join('\n').trim();
    },
    copyRowSummary(r) {
      const parts = [
        `Исход: ${this.shortOutcome(r)}`,
        r.ai_trigger ? `Триггер: ${this.triggerLabel(r.ai_trigger)}` : null,
        this.formatDigestCopy(r) || (r.bot_summary ? `Выжимка: ${r.bot_summary}` : null),
        r.phone ? `Телефон: ${r.phone}` : null,
        r.crm_order_id ? `Заказ: ${this.crmOrderLabel(r)}` : r.sold ? 'Есть заявка' : null,
        r.crm_status_label ? `Статус CRM: ${r.crm_status_label}` : null,
        r.crm_order_id ? this.crmOrderUrl(r) : null,
        r.talkme_url || null,
      ].filter(Boolean);
      this.copyText(parts.join('\n'), 'Кратко скопировано');
    },
    digestOf(row) {
      if (!row) return null;
      if (row.ai_digest && typeof row.ai_digest === 'object') return row.ai_digest;
      if (row.ai_digest_json) {
        try {
          const parsed = typeof row.ai_digest_json === 'string' ? JSON.parse(row.ai_digest_json) : row.ai_digest_json;
          if (parsed && typeof parsed === 'object') return parsed;
        } catch (_) {}
      }
      return null;
    },
    formatDigestCopy(qa) {
      const d = this.digestOf(qa);
      if (!d) return qa && qa.bot_summary ? `Выжимка: ${qa.bot_summary}` : null;
      const lines = [];
      if (d.client_request) lines.push(`Запрос: ${d.client_request}`);
      if (d.summary) lines.push(`Выжимка: ${d.summary}`);
      if (d.result) lines.push(`Итог: ${d.result}`);
      if ((d.confirmed_facts || []).length) {
        lines.push('Факты:');
        d.confirmed_facts.forEach((f) => lines.push(`- ${f}`));
      }
      if ((d.unresolved_issues || []).length) {
        lines.push('Не решено:');
        d.unresolved_issues.forEach((f) => lines.push(`- ${f}`));
      }
      if ((d.service_quality_issues || []).length) {
        lines.push('Качество сервиса:');
        d.service_quality_issues.forEach((f) => lines.push(`- ${f}`));
      }
      return lines.length ? lines.join('\n') : null;
    },
    copyDetailSummary() {
      const qa = (this.detail && this.detail.qa) || {};
      const parts = [
        `Диалог${qa.dialog_id ? ' #' + qa.dialog_id : ''}`,
        `Исход: ${qa.outcome_label || qa.outcome || '—'}`,
        qa.ai_trigger ? `Триггер: ${this.triggerLabel(qa.ai_trigger)}` : null,
        this.formatDigestCopy(qa),
        qa.phone ? `Телефон: ${qa.phone}` : null,
        qa.crm_order_id ? `Заказ: ${this.crmOrderLabel(qa)}` : null,
        qa.crm_status_label ? `Статус CRM: ${qa.crm_status_label}` : null,
        qa.crm_order_id ? this.crmOrderUrl(qa) : null,
        qa.talkme_url || null,
      ].filter(Boolean);
      this.copyText(parts.join('\n'), 'Выжимка скопирована');
    },
    copyDetailChat() {
      this.copyText(this.buildChatText(this.detail), 'Диалог скопирован');
    },
    setOutcome(value) {
      this.outcome = value;
      if (value === 'pending') {
        this.primaryTagFilter = '';
        this.tagFilter = '';
      }
      if (value !== 'fail' && value !== 'all') this.failureClass = '';
      this.page = 1;
      this.reload();
    },
    setTrigger(value) {
      this.trigger = this.trigger === value && value !== 'all' ? 'all' : value;
      this.page = 1;
      this.reload();
    },
    filterFail(key) {
      this.outcome = 'fail';
      this.failureClass = this.failureClass === key ? '' : key;
      this.page = 1;
      this.reload();
    },
    goPage(p) {
      this.page = p;
      this.fetchList();
    },
    closeDetail() {
      this.detail = null;
      this.copyHint = '';
    },
    onDateManual() {
      this.periodPreset = 'custom';
      this.reload();
    },
    applyPeriodPreset(value) {
      this.periodPreset = value;
      this.dateTo = todayYmd();
      if (value === '7d') this.dateFrom = daysAgoYmd(6);
      else if (value === '28d' || value === '30d') this.dateFrom = daysAgoYmd(27);
      else if (value === '90d') this.dateFrom = daysAgoYmd(89);
      else if (value === '12m') this.dateFrom = monthsAgoYmd(12);
      else if (value === '3y') this.dateFrom = yearsAgoYmd(3);
      if (value === '12m' || value === '3y') {
        this.chartGranularity = value === '3y' ? 'year' : 'month';
      } else {
        this.chartGranularity = 'day';
      }
      if (this.compareEnabled) {
        if (this.compareMode === 'prev' || this.compareMode === 'prev_equal') this.syncComparePrevPeriod();
        else if (this.compareMode === 'prev_year') this.syncComparePrevYear();
      }
      this.reload();
    },
    openDateModal(tab) {
      this.dateModalTab = tab === 'compare' ? 'compare' : 'filter';
      this.dateModalFrom = this.dateFrom;
      this.dateModalTo = this.dateTo;
      this.dateModalCompareFrom = this.compareDateFrom || this.dateFrom;
      this.dateModalCompareTo = this.compareDateTo || this.dateTo;
      this.dateModalFilterPreset = this.periodPreset === 'custom' ? 'custom' : (this.periodPreset || '7d');
      if (!['7d', '28d', '90d', '12m', 'custom'].includes(this.dateModalFilterPreset)) {
        this.dateModalFilterPreset = 'custom';
      }
      this.dateModalComparePreset = this.compareEnabled
        ? (this.compareMode === 'custom' ? 'custom' : (this.compareMode || 'prev_equal'))
        : 'prev_equal';
      if (this.dateModalComparePreset === 'prev') this.dateModalComparePreset = 'prev_equal';
      this.dateModalOpen = true;
    },
    closeDateModal() {
      this.dateModalOpen = false;
    },
    disableCompareFromModal() {
      this.compareEnabled = false;
      this.statsCompare = null;
      this.byDayCompare = [];
      this.dateModalOpen = false;
      this.$nextTick(() => this.renderChart());
    },
    resolvePresetRange(preset) {
      const to = todayYmd();
      if (preset === '7d') return { from: daysAgoYmd(6), to, gran: 'day' };
      if (preset === '28d' || preset === '30d') return { from: daysAgoYmd(27), to, gran: 'day' };
      if (preset === '90d') return { from: daysAgoYmd(89), to, gran: 'day' };
      if (preset === '12m') return { from: monthsAgoYmd(12), to, gran: 'month' };
      return { from: this.dateFrom, to: this.dateTo, gran: this.chartGranularity };
    },
    applyDateModal() {
      if (!this.dateModalCanApply) return;
      if (this.dateModalTab === 'filter') {
        if (this.dateModalFilterPreset === 'custom') {
          this.dateFrom = this.dateModalFrom;
          this.dateTo = this.dateModalTo;
          this.periodPreset = 'custom';
        } else {
          const r = this.resolvePresetRange(this.dateModalFilterPreset);
          this.dateFrom = r.from;
          this.dateTo = r.to;
          this.periodPreset = this.dateModalFilterPreset;
          this.chartGranularity = r.gran;
        }
        if (this.compareEnabled && (this.compareMode === 'prev' || this.compareMode === 'prev_equal')) {
          this.syncComparePrevPeriod();
        } else if (this.compareEnabled && this.compareMode === 'prev_year') {
          this.syncComparePrevYear();
        }
      } else {
        // compare tab
        const preset = this.dateModalComparePreset;
        if (preset === '7d_prev') {
          const r = this.resolvePresetRange('7d');
          this.dateFrom = r.from; this.dateTo = r.to; this.periodPreset = '7d';
          this.chartGranularity = 'day';
          this.syncComparePrevPeriod();
          this.compareMode = 'prev_equal';
        } else if (preset === '28d_prev') {
          const r = this.resolvePresetRange('28d');
          this.dateFrom = r.from; this.dateTo = r.to; this.periodPreset = '28d';
          this.chartGranularity = 'day';
          this.syncComparePrevPeriod();
          this.compareMode = 'prev_equal';
        } else if (preset === '90d_prev') {
          const r = this.resolvePresetRange('90d');
          this.dateFrom = r.from; this.dateTo = r.to; this.periodPreset = '90d';
          this.chartGranularity = 'day';
          this.syncComparePrevPeriod();
          this.compareMode = 'prev_equal';
        } else if (preset === 'prev_year') {
          this.compareMode = 'prev_year';
          this.syncComparePrevYear();
        } else if (preset === 'custom') {
          this.compareMode = 'custom';
          this.compareDateFrom = this.dateModalCompareFrom;
          this.compareDateTo = this.dateModalCompareTo;
        } else {
          this.compareMode = 'prev_equal';
          this.syncComparePrevPeriod();
        }
        this.compareEnabled = true;
      }
      this.dateModalOpen = false;
      this.reload();
    },
    syncComparePrevYear() {
      if (!this.dateFrom || !this.dateTo) return;
      this.compareDateFrom = this.shiftYmd(this.dateFrom, -365);
      this.compareDateTo = this.shiftYmd(this.dateTo, -365);
    },
    setChartGranularity(value) {
      this.chartGranularity = value;
      this.$nextTick(() => this.renderChart());
    },
    setChartType(value) {
      if (this.chartType === value) return;
      this.chartType = value;
      this.$nextTick(() => this.renderChart());
    },
    daysBetweenInclusive(from, to) {
      const a = new Date(from + 'T12:00:00Z');
      const b = new Date(to + 'T12:00:00Z');
      if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
      return Math.max(0, Math.round((b - a) / 86400000));
    },
    shiftYmd(ymd, deltaDays) {
      const d = new Date(ymd + 'T12:00:00Z');
      d.setUTCDate(d.getUTCDate() + deltaDays);
      return d.toISOString().slice(0, 10);
    },
    syncComparePrevPeriod() {
      if (!this.dateFrom || !this.dateTo) return;
      const span = this.daysBetweenInclusive(this.dateFrom, this.dateTo);
      this.compareDateTo = this.shiftYmd(this.dateFrom, -1);
      this.compareDateFrom = this.shiftYmd(this.compareDateTo, -span);
    },
    isMetricActive(key) {
      return (this.activeChartMetrics || []).indexOf(key) !== -1;
    },
    toggleChartMetric(key) {
      const list = this.activeChartMetrics.slice();
      const i = list.indexOf(key);
      if (i === -1) list.push(key);
      else {
        if (list.length === 1) return; // keep at least one
        list.splice(i, 1);
      }
      this.activeChartMetrics = list;
      this.$nextTick(() => this.renderChart());
    },
    formatMetricTotal(key, totals) {
      const t = totals || {};
      if (key === 'phone_pct') {
        const base = Number(t.real_dialogs || t.total) || 0;
        if (!base) return '—';
        return Math.round((100 * (Number(t.with_phone) || 0)) / base) + '%';
      }
      const n = Number(t[key]) || 0;
      if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 1 : 2).replace(/\.0$/, '') + ' тыс.';
      return String(n);
    },
    metricValue(row, metric) {
      if (!row) return 0;
      if (metric === 'phone_pct') {
        const total = Number(row.total) || 0;
        if (!total) return null;
        return Math.round((100 * (Number(row.with_phone) || 0)) / total);
      }
      return Number(row[metric]) || 0;
    },
    metricLabel(metric) {
      const def = (this.chartMetricDefs || []).find((m) => m.key === metric);
      if (def) return def.label;
      return ({
        breakdown: 'Исходы',
        total: 'Всего',
        success: 'Успех',
        consult: 'Консультация',
        fail: 'Провал',
        with_phone: 'С телефоном',
        phone_pct: '% с телефоном',
        sold: 'Заявки',
        pending: 'Ждут ИИ',
        with_order: 'Заказы CRM',
        tag_successful: 'Успешный',
        tag_failed: 'Неуспешный',
        tag_insufficient: 'Недостаточно информации',
        trigger_purchase: 'Покупка',
        trigger_warranty: 'Гарантия',
        trigger_non_target: 'Нецелевой',
      })[metric] || metric;
    },
    filterListByChartKey(key) {
      if (!key) return;
      if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
        this.dateFrom = key;
        this.dateTo = key;
        this.periodPreset = 'custom';
        this.chartGranularity = 'day';
        this.reload();
        this.showToast('Список: ' + this.formatDay(key));
        return;
      }
      if (/^\d{4}-\d{2}$/.test(key)) {
        const parts = key.split('-').map(Number);
        const y = parts[0];
        const m = parts[1];
        const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
        this.dateFrom = key + '-01';
        this.dateTo = key + '-' + String(last).padStart(2, '0');
        this.periodPreset = 'custom';
        this.reload();
        this.showToast('Список: ' + this.formatChartLabel(key));
        return;
      }
      if (/^\d{4}$/.test(key)) {
        this.dateFrom = key + '-01-01';
        this.dateTo = key + '-12-31';
        this.periodPreset = 'custom';
        this.reload();
        this.showToast('Список: ' + key);
      }
    },
    rollupByGranularity(rows, mode, from, to) {
      const map = {};
      (rows || []).forEach((row) => {
        const day = String(row.day || '').slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return;
        let key = day;
        if (mode === 'month') key = day.slice(0, 7);
        else if (mode === 'year') key = day.slice(0, 4);
        if (!map[key]) {
          map[key] = {
            key, total: 0, success: 0, consult: 0, fail: 0, sold: 0, with_phone: 0,
            with_order: 0, pending: 0,
            tag_successful: 0, tag_failed: 0, tag_insufficient: 0,
            trigger_purchase: 0, trigger_warranty: 0, trigger_non_target: 0,
          };
        }
        map[key].total += Number(row.total) || 0;
        map[key].success += Number(row.success) || 0;
        map[key].consult += Number(row.consult) || 0;
        map[key].fail += Number(row.fail) || 0;
        map[key].sold += Number(row.sold) || 0;
        map[key].with_phone += Number(row.with_phone) || 0;
        map[key].with_order += Number(row.with_order) || 0;
        map[key].pending += Number(row.pending) || 0;
        map[key].tag_successful += Number(row.tag_successful) || 0;
        map[key].tag_failed += Number(row.tag_failed) || 0;
        map[key].tag_insufficient += Number(row.tag_insufficient) || 0;
        map[key].trigger_purchase += Number(row.trigger_purchase) || 0;
        map[key].trigger_warranty += Number(row.trigger_warranty) || 0;
        map[key].trigger_non_target += Number(row.trigger_non_target) || 0;
      });
      let series = Object.values(map).sort((a, b) => a.key.localeCompare(b.key));
      if (from && to) {
        series = this.fillMissingKeys(series, from, to, mode);
      }
      return series;
    },
    fillMissingKeys(series, from, to, mode) {
      const byKey = {};
      (series || []).forEach((r) => { byKey[r.key] = r; });
      const empty = (key) => ({
        key, total: 0, success: 0, consult: 0, fail: 0, sold: 0, with_phone: 0,
        with_order: 0, pending: 0,
        tag_successful: 0, tag_failed: 0, tag_insufficient: 0,
        trigger_purchase: 0, trigger_warranty: 0, trigger_non_target: 0,
      });
      const out = [];
      if (mode === 'year') {
        const y0 = Number(String(from).slice(0, 4));
        const y1 = Number(String(to).slice(0, 4));
        if (!y0 || !y1) return series;
        for (let y = y0; y <= y1 && out.length < 40; y += 1) {
          const key = String(y);
          out.push(byKey[key] || empty(key));
        }
        return out;
      }
      if (mode === 'month') {
        let y = Number(String(from).slice(0, 4));
        let m = Number(String(from).slice(5, 7));
        const yEnd = Number(String(to).slice(0, 4));
        const mEnd = Number(String(to).slice(5, 7));
        if (!y || !m || !yEnd || !mEnd) return series;
        let guard = 0;
        while ((y < yEnd || (y === yEnd && m <= mEnd)) && guard < 240) {
          const key = y + '-' + String(m).padStart(2, '0');
          out.push(byKey[key] || empty(key));
          m += 1;
          if (m > 12) { m = 1; y += 1; }
          guard += 1;
        }
        return out;
      }
      return this.fillMissingDays(series, from, to);
    },
    fillMissingDays(series, from, to) {
      const byKey = {};
      (series || []).forEach((r) => { byKey[r.key] = r; });
      const out = [];
      const start = new Date(from + 'T12:00:00Z');
      const end = new Date(to + 'T12:00:00Z');
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return series;
      const cur = new Date(start);
      let guard = 0;
      while (cur <= end && guard < 800) {
        const key = cur.toISOString().slice(0, 10);
        out.push(byKey[key] || {
          key, total: 0, success: 0, consult: 0, fail: 0, sold: 0, with_phone: 0,
          with_order: 0, pending: 0,
          tag_successful: 0, tag_failed: 0, tag_insufficient: 0,
          trigger_purchase: 0, trigger_warranty: 0, trigger_non_target: 0,
        });
        cur.setUTCDate(cur.getUTCDate() + 1);
        guard += 1;
      }
      return out;
    },
    formatChartLabel(key) {
      if (!key) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
        const parts = key.split('-');
        return parts[2] + '.' + parts[1];
      }
      if (/^\d{4}-\d{2}$/.test(key)) {
        const parts = key.split('-');
        const names = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
        return (names[Number(parts[1]) - 1] || parts[1]) + ' ' + parts[0];
      }
      return key;
    },
    destroyChart() {
      if (this.statsChart) {
        try {
          this.statsChart.destroy();
        } catch (_) { /* ignore */ }
        this.statsChart = null;
      }
    },
    buildChartDatasets(series, seriesB) {
      const type = this.chartType;
      const metrics = (this.activeChartMetrics || []).slice();
      const defs = {};
      (this.chartMetricDefs || []).forEach((m) => { defs[m.key] = m; });
      const pointRadius = series.length > 40 ? 0 : 2.5;
      const hasPct = metrics.indexOf('phone_pct') !== -1;
      const hasCount = metrics.some((k) => k !== 'phone_pct');
      const compare = this.compareEnabled;
      const len = compare ? Math.max(series.length, seriesB.length) : series.length;
      const labels = compare
        ? Array.from({ length: len }, (_, i) => String(i + 1))
        : series.map((r) => this.formatChartLabel(r.key));

      const datasets = [];
      metrics.forEach((key) => {
        const def = defs[key] || { label: key, color: '#1a73e8' };
        const isPct = key === 'phone_pct';
        const yAxisID = isPct ? 'y-pct' : 'y-count';
        const dataA = [];
        for (let i = 0; i < len; i += 1) {
          dataA.push(this.metricValue(series[i], key));
        }
        datasets.push({
          type,
          label: def.label,
          data: dataA,
          borderColor: def.color,
          backgroundColor: type === 'bar' ? def.color : 'transparent',
          borderWidth: type === 'line' ? 2.5 : 0,
          pointRadius: type === 'line' ? pointRadius : 0,
          pointHoverRadius: 4,
          fill: false,
          lineTension: 0.15,
          yAxisID,
        });
        if (compare) {
          const dataB = [];
          for (let i = 0; i < len; i += 1) {
            dataB.push(this.metricValue(seriesB[i], key));
          }
          datasets.push({
            type,
            label: def.label + ' (сравн.)',
            data: dataB,
            borderColor: def.color,
            backgroundColor: type === 'bar' ? def.color : 'transparent',
            borderWidth: type === 'line' ? 2.5 : 0,
            borderDash: [7, 5],
            pointRadius: type === 'line' ? Math.max(pointRadius, 2) : 0,
            pointHoverRadius: 4,
            fill: false,
            lineTension: 0.15,
            yAxisID,
          });
        }
      });

      return {
        labels,
        datasets,
        compare,
        series,
        seriesB: seriesB || [],
        hasPct,
        hasCount,
        metric: metrics[0] || 'total',
        isPct: metrics.length === 1 && metrics[0] === 'phone_pct',
      };
    },
    renderChart() {
      this.destroyChart();
      const canvas = this.$refs.statsChart;
      if (!canvas) return;
      const series = this.chartSeries;
      if (!series.length) return;
      const seriesB = this.chartSeriesCompare;
      const built = this.buildChartDatasets(series, seriesB);
      const stacked = false;
      const self = this;

      this.statsChart = new Chart(canvas, {
        type: this.chartType,
        data: {
          labels: built.labels,
          datasets: built.datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          legend: {
            display: true,
            position: 'bottom',
            labels: { boxWidth: 12, fontSize: 11, padding: 12 },
          },
          onClick(evt, elements) {
            if (!elements || !elements.length) return;
            const idx = elements[0]._index;
            const row = built.series[idx];
            if (row) self.filterListByChartKey(row.key);
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              title(items) {
                if (!items || !items.length) return '';
                const idx = items[0].index;
                if (built.compare) {
                  const a = built.series[idx];
                  const b = built.seriesB[idx];
                  const left = a ? self.formatChartLabel(a.key) : '—';
                  const right = b ? self.formatChartLabel(b.key) : '—';
                  return '#' + (idx + 1) + ': A ' + left + '  ·  B ' + right;
                }
                const row = built.series[idx];
                return row ? self.formatChartLabel(row.key) : '';
              },
              afterBody(items) {
                if (!items || !items.length || built.compare) return '';
                const idx = items[0].index;
                const row = built.series[idx];
                if (!row) return '';
                const lines = ['Всего: ' + row.total, 'С телефоном: ' + (row.with_phone || 0)];
                if (row.sold) lines.push('Заявки: ' + row.sold);
                return lines;
              },
              label(tooltipItem, data) {
                const ds = data.datasets[tooltipItem.datasetIndex];
                const val = tooltipItem.yLabel;
                if (ds && ds.yAxisID === 'y-pct') {
                  return ds.label + ': ' + (val == null ? '—' : val + '%');
                }
                return ds.label + ': ' + (val == null ? '—' : val);
              },
            },
          },
          scales: {
            xAxes: [{
              stacked,
              ticks: {
                maxRotation: 0,
                autoSkip: true,
                maxTicksLimit: this.chartGranularity === 'day' ? 14 : 24,
                fontSize: 10,
              },
              gridLines: { display: false },
            }],
            yAxes: [
              {
                id: 'y-count',
                stacked: false,
                display: built.hasCount,
                position: 'left',
                ticks: { beginAtZero: true, precision: 0, fontSize: 11 },
                gridLines: { color: 'rgba(60,64,67,0.08)' },
                scaleLabel: { display: built.hasCount, labelString: 'Диалоги', fontSize: 10 },
              },
              {
                id: 'y-pct',
                display: built.hasPct,
                position: 'right',
                ticks: {
                  beginAtZero: true,
                  max: 100,
                  fontSize: 11,
                  callback(v) { return v + '%'; },
                },
                gridLines: { drawOnChartArea: !built.hasCount },
                scaleLabel: { display: built.hasPct, labelString: '%', fontSize: 10 },
              },
            ],
          },
        },
      });
    },
    ratingQueryParams() {
      const f = this.ratingFilter;
      if (!f) return {};
      if (f === 'unrated' || f === 'rated') return { ratingFilter: f };
      if (f === '1-2') return { ratingMin: '1', ratingMax: '2' };
      if (f === '3') return { ratingMin: '3', ratingMax: '3' };
      if (f === '4-5') return { ratingMin: '4', ratingMax: '5' };
      return {};
    },
    isManagerMessage(m) {
      if (!m) return false;
      if (m.role === 'operator' || m.role === 'manager' || m.role === 'human') return true;
      const name = String(m.name || m.author || '').trim();
      if (!name) return false;
      const low = name.toLowerCase();
      if (low === 'bot' || low === 'assistant' || low === 'михаил') return false;
      if (name.indexOf('@') !== -1) return true;
      return true;
    },
    bubbleAuthor(m) {
      if (!m) return 'Сообщение';
      if (m.role === 'user') return 'Клиент';
      if (this.isManagerMessage(m)) {
        const name = String(m.name || m.author || '').trim();
        return name ? ('Менеджер · ' + name) : 'Менеджер';
      }
      return 'Бот';
    },
    bubbleClass(m) {
      if (!m) return 'is-bot';
      if (m.role === 'user') return 'is-user';
      if (this.isManagerMessage(m)) return 'is-manager';
      return 'is-bot';
    },
    applyAnnotationResult(clientId, data) {
      const patch = {
        manager_rating: data.manager_rating,
        manager_tags: data.manager_tags || [],
        manager_annotated_at: data.manager_annotated_at,
        qc_verdict: data.qc_verdict,
        qc_disagree_reason: data.qc_disagree_reason,
      };
      if (Object.prototype.hasOwnProperty.call(data, 'ai_rating')) {
        patch.ai_rating = data.ai_rating;
      }
      if (this.detail && this.detail.qa && this.detail.qa.client_id === clientId) {
        Object.keys(patch).forEach((k) => {
          this.$set(this.detail.qa, k, patch[k]);
        });
      }
      const idx = this.rows.findIndex((r) => r.client_id === clientId);
      if (idx !== -1) {
        this.$set(this.rows, idx, { ...this.rows[idx], ...patch });
      }
    },
    async saveAnnotationFor(clientId, payload) {
      if (!clientId) return null;
      this.annotating = true;
      try {
        const res = await fetch(`${API_BASE}/bot-dialog-qa-annotate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_id: clientId,
            ...payload,
          }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'annotate error');
        this.applyAnnotationResult(clientId, data);
        return data;
      } catch (e) {
        this.showToast('Не удалось сохранить: ' + e.message);
        return null;
      } finally {
        this.annotating = false;
      }
    },
    async agreeWithAiRating(row) {
      if (!row || !row.client_id) return;
      if (row.ai_rating == null) {
        this.showToast('Сначала нужна оценка агента');
        return;
      }
      const data = await this.saveAnnotationFor(row.client_id, {
        qc_verdict: 'agree',
        rating: row.ai_rating,
      });
      if (data) this.showToast('Согласие с оценкой агента сохранено');
    },
    startQcDisagree(row) {
      if (!row || !row.client_id) return;
      this.qcDisagreeFor = row.client_id;
      this.qcDisagreeRating = row.manager_rating != null ? Number(row.manager_rating) : null;
      this.qcDisagreeReason = row.qc_disagree_reason || '';
    },
    cancelQcDisagree() {
      this.qcDisagreeFor = null;
      this.qcDisagreeRating = null;
      this.qcDisagreeReason = '';
    },
    async submitQcDisagree(row) {
      if (!row || !row.client_id) return;
      const reason = String(this.qcDisagreeReason || '').trim();
      if (!this.qcDisagreeRating || !reason) {
        this.showToast('Нужны оценка 1–5 и причина');
        return;
      }
      const data = await this.saveAnnotationFor(row.client_id, {
        qc_verdict: 'disagree',
        rating: this.qcDisagreeRating,
        qc_disagree_reason: reason,
      });
      if (data) {
        this.cancelQcDisagree();
        this.showToast('Несогласие и оценка ОК сохранены');
      }
    },
    async reload() {
      this.page = 1;
      if (this.compareEnabled) {
        if (this.compareMode === 'prev' || this.compareMode === 'prev_equal') {
          this.syncComparePrevPeriod();
        } else if (this.compareMode === 'prev_year') {
          this.syncComparePrevYear();
        }
      }
      await Promise.all([
        this.fetchStats({ skipChart: true }),
        this.fetchList(),
        this.compareEnabled ? this.fetchCompareStats({ skipChart: true }) : Promise.resolve(),
      ]);
      this.$nextTick(() => this.renderChart());
    },
    async fetchStats(opts = {}) {
      const qs = new URLSearchParams({
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
      });
      const res = await fetch(API_BASE + '/bot-dialog-qa-stats?' + qs);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'stats error');
      this.stats = data.totals || {};
      this.byFailure = data.byFailure || [];
      this.byTrigger = data.byTrigger || [];
      this.byDay = data.byDay || [];
      if (Array.isArray(data.tagPresets) && data.tagPresets.length) {
        this.tagPresets = data.tagPresets;
      }
      if (Array.isArray(data.manualTagPresets) && data.manualTagPresets.length) {
        this.manualTagPresets = data.manualTagPresets;
      }
      this.maxFailCnt = Math.max(1, ...this.byFailure.map((x) => x.cnt || 0));
      this.lastSync = (data.last_sync && (data.last_sync.updated_at || data.last_sync.value)) || null;
      if (!opts.skipChart) this.$nextTick(() => this.renderChart());
    },
    async fetchCompareStats(opts = {}) {
      if (!this.compareEnabled || !this.compareDateFrom || !this.compareDateTo) {
        this.byDayCompare = [];
        this.statsCompare = null;
        if (!opts.skipChart) this.$nextTick(() => this.renderChart());
        return;
      }
      try {
        const qs = new URLSearchParams({
          dateFrom: this.compareDateFrom,
          dateTo: this.compareDateTo,
        });
        const res = await fetch(API_BASE + '/bot-dialog-qa-stats?' + qs);
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'compare stats error');
        this.byDayCompare = data.byDay || [];
        this.statsCompare = data.totals || null;
      } catch (e) {
        console.error(e);
        this.byDayCompare = [];
        this.statsCompare = null;
        this.showToast('Не удалось загрузить период B: ' + e.message);
      }
      if (!opts.skipChart) this.$nextTick(() => this.renderChart());
    },
    async fetchList() {
      this.loading = true;
      try {
        const qs = new URLSearchParams({
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          outcome: this.outcome,
          trigger: this.trigger,
          page: String(this.page),
          perPage: String(this.perPage),
        });
        if (this.failureClass) qs.set('failureClass', this.failureClass);
        if (this.onlySold) qs.set('sold', '1');
        if (this.onlyOrder) qs.set('hasOrder', '1');
        if (this.onlyHasPhone) qs.set('hasPhone', '1');
        const tag = this.effectiveTagFilter;
        if (tag) qs.set('tag', tag);
        const ratingParams = this.ratingQueryParams();
        Object.keys(ratingParams).forEach((k) => qs.set(k, ratingParams[k]));
        if (this.q) qs.set('q', this.q);
        const res = await fetch(`${API_BASE}/bot-dialog-qa-list?${qs}`);
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'list error');
        this.rows = data.rows || [];
        this.total = data.total || 0;
        if (Array.isArray(data.tagPresets) && data.tagPresets.length) {
          this.tagPresets = data.tagPresets;
        }
        if (Array.isArray(data.manualTagPresets) && data.manualTagPresets.length) {
          this.manualTagPresets = data.manualTagPresets;
        }
        await this.enrichCrmOrders(this.rows);
      } catch (e) {
        console.error(e);
        this.showToast('Ошибка загрузки: ' + e.message);
      } finally {
        this.loading = false;
      }
    },
    async saveAnnotation(payload) {
      if (!this.detail || !this.detail.qa || !this.detail.qa.client_id) return;
      await this.saveAnnotationFor(this.detail.qa.client_id, payload);
    },
    setDetailRating(n) {
      const payload = { rating: n };
      if (n == null) payload.qc_verdict = null;
      this.saveAnnotation(payload);
    },
    toggleDetailTag(code) {
      const current = [...((this.detail && this.detail.qa && this.detail.qa.manager_tags) || [])];
      const i = current.indexOf(code);
      if (i === -1) current.push(code);
      else current.splice(i, 1);
      this.saveAnnotation({ tags: current });
    },
    async openEvalConfig() {
      this.evalConfigOpen = true;
      this.evalConfigMeta = '';
      try {
        const res = await fetch(`${API_BASE}/bot-dialog-qa-eval-config`);
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'config error');
        this.evalPromptDraft = data.prompt || '';
        this.evalDefaultPrompt = data.defaultPrompt || '';
        this.evalTagsPromptDraft = data.tagsPrompt || '';
        this.evalDefaultTagsPrompt = data.defaultTagsPrompt || '';
        this.evalTagsDraft = (data.aiTags || []).map((t) => ({
          code: t.code || '',
          label: t.label || '',
          hint: t.hint || '',
        }));
        this.evalDefaultTags = (data.defaultAiTags || []).map((t) => ({ ...t, hint: t.hint || '' }));
        this.evalManualTagsDraft = (data.manualTags || []).map((t) => ({
          code: t.code || '',
          label: t.label || '',
          hint: t.hint || '',
        }));
        this.evalDefaultManualTags = (data.defaultManualTags || []).map((t) => ({ ...t, hint: t.hint || '' }));
        this.evalConfigMeta =
          data.isCustomPrompt || data.isCustomTagsPrompt || data.isCustomTags || data.isCustomManualTags
            ? `Сохранено${data.updated_at ? ': ' + this.formatIso(data.updated_at) : ''}`
            : 'Сейчас дефолтные промпты/теги';
        if (Array.isArray(data.aiTags) && data.aiTags.length) {
          this.tagPresets = data.aiTags;
        }
        if (Array.isArray(data.manualTags) && data.manualTags.length) {
          this.manualTagPresets = data.manualTags;
        }
      } catch (e) {
        this.showToast('Не удалось загрузить промпт: ' + e.message);
        this.evalConfigOpen = false;
      }
    },
    closeEvalConfig() {
      this.evalConfigOpen = false;
    },
    addEvalTag() {
      let code = String(this.newTagCode || '').trim();
      if (/[а-яё]/i.test(code)) {
        code = code.replace(/\s+/g, ' ').slice(0, 80);
      } else {
        code = code
          .toLowerCase()
          .replace(/[\s\-]+/g, '_')
          .replace(/[^a-z0-9_]/g, '');
      }
      const label = String(this.newTagLabel || code).trim();
      if (!code || !label) {
        this.showToast('Укажите тег и название');
        return;
      }
      if (this.evalTagsDraft.some((t) => t.code === code)) {
        this.showToast('Такой тег уже есть');
        return;
      }
      this.evalTagsDraft.push({
        code,
        label,
        hint: String(this.newTagHint || '').trim(),
      });
      this.newTagCode = '';
      this.newTagLabel = '';
      this.newTagHint = '';
    },
    removeEvalTag(idx) {
      this.evalTagsDraft.splice(idx, 1);
    },
    addManualTag() {
      let code = String(this.newManualTagCode || '').trim();
      if (/[а-яё]/i.test(code)) {
        code = code.replace(/\s+/g, ' ').slice(0, 80);
      } else {
        code = code
          .toLowerCase()
          .replace(/[\s\-]+/g, '_')
          .replace(/[^a-z0-9_]/g, '');
      }
      const label = String(this.newManualTagLabel || code).trim();
      if (!code || !label) {
        this.showToast('Укажите код и название ручного тега');
        return;
      }
      if (this.evalManualTagsDraft.some((t) => t.code === code)) {
        this.showToast('Такой ручной тег уже есть');
        return;
      }
      this.evalManualTagsDraft.push({
        code,
        label,
        hint: String(this.newManualTagHint || '').trim(),
      });
      this.newManualTagCode = '';
      this.newManualTagLabel = '';
      this.newManualTagHint = '';
    },
    removeManualTag(idx) {
      this.evalManualTagsDraft.splice(idx, 1);
    },
    async saveEvalConfig() {
      this.evalConfigSaving = true;
      try {
        const res = await fetch(`${API_BASE}/bot-dialog-qa-eval-config`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: this.evalPromptDraft,
            tagsPrompt: this.evalTagsPromptDraft,
            aiTags: this.evalTagsDraft,
            manualTags: this.evalManualTagsDraft,
          }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'save error');
        this.tagPresets = data.aiTags || this.evalTagsDraft;
        this.manualTagPresets = data.manualTags || this.evalManualTagsDraft;
        this.evalConfigMeta = 'Сохранено';
        this.showToast('Промпты и теги сохранены');
      } catch (e) {
        this.showToast('Ошибка сохранения: ' + e.message);
      } finally {
        this.evalConfigSaving = false;
      }
    },
    async resetEvalPrompt() {
      this.evalConfigSaving = true;
      try {
        const res = await fetch(`${API_BASE}/bot-dialog-qa-eval-config`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ resetPrompt: true }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'reset error');
        this.evalPromptDraft = data.prompt || this.evalDefaultPrompt;
        this.showToast('Промпт выжимки сброшен');
      } catch (e) {
        this.showToast(e.message);
      } finally {
        this.evalConfigSaving = false;
      }
    },
    async resetEvalTagsPrompt() {
      this.evalConfigSaving = true;
      try {
        const res = await fetch(`${API_BASE}/bot-dialog-qa-eval-config`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ resetTagsPrompt: true }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'reset error');
        this.evalTagsPromptDraft = data.tagsPrompt || this.evalDefaultTagsPrompt;
        this.showToast('Промпт тегов сброшен');
      } catch (e) {
        this.showToast(e.message);
      } finally {
        this.evalConfigSaving = false;
      }
    },
    async resetEvalTags() {
      this.evalConfigSaving = true;
      try {
        const res = await fetch(`${API_BASE}/bot-dialog-qa-eval-config`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ resetTags: true }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'reset error');
        this.evalTagsDraft = (data.aiTags || this.evalDefaultTags).map((t) => ({
          code: t.code || '',
          label: t.label || '',
          hint: t.hint || '',
        }));
        this.tagPresets = data.aiTags || this.evalTagsDraft;
        this.showToast('Справочник тегов ИИ сброшен');
      } catch (e) {
        this.showToast(e.message);
      } finally {
        this.evalConfigSaving = false;
      }
    },
    async resetManualTags() {
      this.evalConfigSaving = true;
      try {
        const res = await fetch(`${API_BASE}/bot-dialog-qa-eval-config`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ resetManualTags: true }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'reset error');
        this.evalManualTagsDraft = (data.manualTags || this.evalDefaultManualTags).map((t) => ({
          code: t.code || '',
          label: t.label || '',
          hint: t.hint || '',
        }));
        this.manualTagPresets = data.manualTags || this.evalManualTagsDraft;
        this.showToast('Ручные теги сброшены');
      } catch (e) {
        this.showToast(e.message);
      } finally {
        this.evalConfigSaving = false;
      }
    },
    async runSync() {
      this.syncing = true;
      try {
        const res = await fetch(`${API_BASE}/bot-dialog-qa-sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dateFrom: this.dateFrom,
            dateTo: this.dateTo,
            force: false,
          }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'sync error');
        await this.reload();
        this.showToast('Диалоги подтянуты');
      } catch (e) {
        this.showToast('Ошибка синхронизации: ' + e.message);
      } finally {
        this.syncing = false;
      }
    },
    isEvaluating(clientId) {
      return !!clientId && this.evaluatingClientId === clientId;
    },
    async runAiEvalOne(row) {
      const clientId = row && row.client_id;
      if (!clientId || this.evaluatingClientId || this.aiRunning) return;
      this.evaluatingClientId = clientId;
      try {
        const res = await fetch(`${API_BASE}/bot-dialog-qa-ai-eval-one`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ client_id: clientId }),
        });
        const raw = await res.text();
        let data;
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch (_) {
          throw new Error(
            res.ok
              ? 'Сервер вернул не JSON'
              : `HTTP ${res.status}: ответ не JSON (проверьте nginx/прокси)`
          );
        }
        if (!res.ok || !data.success) {
          throw new Error(data.error || data.reason || `HTTP ${res.status}`);
        }
        const result = data.result || {};
        const label = result.outcome_label || result.outcome || 'ok';
        this.showToast(`ИИ оценил: ${label}`);
        // Обновляем список и, если открыта карточка этого диалога, — деталку
        await this.reload();
        if (this.detail && this.detail.qa && this.detail.qa.client_id === clientId) {
          const fresh = this.rows.find((r) => r.client_id === clientId) || row;
          await this.openDetail(fresh);
        }
      } catch (e) {
        this.showToast('Ошибка оценки: ' + e.message);
      } finally {
        this.evaluatingClientId = null;
      }
    },
    async runAiEval() {
      this.aiRunning = true;
      try {
        const res = await fetch(`${API_BASE}/bot-dialog-qa-ai-eval`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dateFrom: this.dateFrom,
            dateTo: this.dateTo,
            limit: 25,
            only_idle: false,
            // Пересчитать уже оценённые диалоги новым промптом (иначе берутся только «дыры»)
            force: true,
          }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'ai eval error');
        if (data.already_running) {
          this.showToast('Оценка уже идёт — ждём…');
        } else {
          this.showToast('Оценка запущена в фоне…');
        }
        await this.pollAiEvalUntilDone();
      } catch (e) {
        this.showToast('Ошибка оценки ИИ: ' + e.message);
        this.aiRunning = false;
      }
    },
    async pollAiEvalUntilDone() {
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      const maxWaitMs = 15 * 60 * 1000;
      const started = Date.now();
      while (Date.now() - started < maxWaitMs) {
        await sleep(2500);
        try {
          const res = await fetch(`${API_BASE}/bot-dialog-qa-ai-eval-status`);
          const data = await res.json();
          const job = (data && data.job) || {};
          if (job.status === 'done') {
            this.showToast(`ИИ оценил: ${job.ok || 0} из ${job.checked || 0}`);
            this.aiRunning = false;
            await this.reload();
            return;
          }
          if (job.status === 'error') {
            throw new Error(job.error || 'ai eval failed');
          }
          if (job.status !== 'running' && job.status !== 'idle') {
            // неизвестный статус — не крутим вечно
            break;
          }
        } catch (e) {
          this.showToast('Ошибка оценки ИИ: ' + e.message);
          this.aiRunning = false;
          return;
        }
      }
      this.showToast('Оценка ещё идёт на сервере — обновите список позже');
      this.aiRunning = false;
    },
    async openDetail(r) {
      try {
        const res = await fetch(`${API_BASE}/bot-dialog-qa-detail/${encodeURIComponent(r.client_id)}`);
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'detail error');
        if (data.qa) {
          // переносим уже известные CRM-поля из списка, затем дотягиваем свежие
          if (r.crm_order_number) data.qa.crm_order_number = r.crm_order_number;
          if (r.crm_status_label) data.qa.crm_status_label = r.crm_status_label;
          if (r.crm_status) data.qa.crm_status = r.crm_status;
          await this.enrichCrmOrders([data.qa]);
        }
        this.detail = data;
        this.copyHint = '';
      } catch (e) {
        this.showToast(e.message);
      }
    },
  },
};
</script>

<style scoped>
.bqa {
  --bg: #f3f5f8;
  --panel: #ffffff;
  --line: #e3e8ef;
  --text: #1c2430;
  --muted: #6b7785;
  --blue: #2563eb;
  --blue-soft: #eaf1ff;
  --ok: #0f7a45;
  --ok-soft: #e8f7ef;
  --bad: #c03535;
  --bad-soft: #fdecec;
  --warn: #9a6700;
  --radius: 12px;
  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
  padding: 20px 24px 56px;
  box-sizing: border-box;
}

.bqa-top {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.bqa-top__left {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.bqa-back {
  color: var(--muted);
  text-decoration: none;
  font-size: 13px;
  margin-top: 8px;
  white-space: nowrap;
}

.bqa-back:hover { color: var(--blue); }

.bqa-title {
  margin: 0 0 4px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.bqa-subtitle {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.4;
  max-width: 560px;
}

.bqa-top__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.bqa-filters,
.bqa-perf,
.bqa-chart-panel,
.bqa-reasons,
.bqa-list-panel {
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  margin-bottom: 14px;
}

.bqa-filters { padding: 14px 16px; }

.bqa-pills--compact {
  margin-top: 8px;
}

.bqa-chart-panel {
  padding: 14px 16px 10px;
}

.bqa-period-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.bqa-period-chip {
  border: 1px solid #dadce0;
  background: #fff;
  color: #3c4043;
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.bqa-period-chip.is-active {
  background: #e8f0fe;
  border-color: #d2e3fc;
  color: #1967d2;
}

.bqa-period-chip--compare {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.bqa-period-chip__caret {
  font-size: 10px;
  opacity: 0.7;
}

.bqa-period-range {
  border: 0;
  background: transparent;
  color: #5f6368;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 6px;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.bqa-perf {
  padding: 16px 16px 12px;
}

.bqa-perf__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.bqa-perf__title {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: #202124;
}

.bqa-perf__updated {
  margin: 4px 0 0;
  color: #5f6368;
  font-size: 12px;
}

.bqa-perf__tools {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.bqa-seg {
  display: inline-flex;
  border: 1px solid #dadce0;
  border-radius: 999px;
  overflow: hidden;
  background: #fff;
}

.bqa-seg__btn {
  border: 0;
  background: transparent;
  padding: 7px 12px;
  font-size: 12px;
  color: #3c4043;
  cursor: pointer;
}

.bqa-seg__btn.is-active {
  background: #e8f0fe;
  color: #1967d2;
  font-weight: 600;
}

.bqa-metric-bar {
  display: flex;
  align-items: stretch;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.bqa-metric-dd {
  position: relative;
  flex: 1 1 420px;
  min-width: 280px;
}

.bqa-metric-dd__btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #dadce0;
  background: #fff;
  border-radius: 10px;
  padding: 12px 16px;
  cursor: pointer;
  text-align: left;
  min-height: 52px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.bqa-metric-dd__btn:hover {
  border-color: #b0b5bb;
}

.bqa-metric-dd.is-open .bqa-metric-dd__btn {
  border-color: #1a73e8;
  box-shadow: 0 0 0 1px #1a73e8;
}

.bqa-metric-dd__btn-label {
  font-size: 11px;
  font-weight: 700;
  color: #5f6368;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.bqa-metric-dd__btn-summary {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #202124;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bqa-metric-dd__caret {
  color: #5f6368;
  font-size: 11px;
  line-height: 1;
}

.bqa-metric-dd__panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 40;
  display: flex;
  align-items: stretch;
  width: max(840px, 100%);
  max-width: min(960px, 96vw);
  background: #fff;
  border: 1px solid #dadce0;
  border-radius: 12px;
  box-shadow: 0 12px 36px rgba(32, 33, 36, 0.16);
  overflow: hidden;
}

.bqa-metric-dd__col {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 14px 12px 12px;
  border-right: 1px solid #eef0f3;
  min-width: 220px;
}

.bqa-metric-dd__col:last-child { border-right: 0; }

.bqa-metric-dd__col-title {
  font-size: 11px;
  font-weight: 700;
  color: #80868b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0 8px 10px;
  margin: 0 0 6px;
  line-height: 1.25;
  border-bottom: 1px solid #f1f3f4;
}

.bqa-metric-dd__opt {
  display: flex !important;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 42px;
  padding: 8px 10px;
  margin: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  transition: background 0.12s ease;
}

.bqa-metric-dd__opt:hover {
  background: #f8f9fa;
}

.bqa-metric-dd__opt.is-on {
  background: var(--metric-bg, #e8f0fe);
}

.bqa-metric-dd__check {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bqa-metric-dd__check-box {
  width: 15px;
  height: 15px;
  border: 1.5px solid #9aa0a6;
  border-radius: 3px;
  background: #fff;
  box-sizing: border-box;
}

.bqa-metric-dd__opt.is-on .bqa-metric-dd__check-box {
  border-color: var(--metric, #1a73e8);
  background: var(--metric, #1a73e8);
  box-shadow: inset 0 0 0 2px #fff;
}

.bqa-metric-dd__opt-label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  line-height: 1.3;
  color: #202124;
  white-space: normal;
  word-break: normal;
  overflow-wrap: anywhere;
}

.bqa-metric-dd__opt.is-on .bqa-metric-dd__opt-label {
  color: var(--metric, #1a73e8);
  font-weight: 600;
}

.bqa-metric-dd__opt-value {
  flex: 0 0 auto;
  margin-left: auto;
  font-size: 14px;
  font-weight: 700;
  color: #202124;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  white-space: nowrap;
}

.bqa-metric-dd__opt.is-on .bqa-metric-dd__opt-value {
  color: var(--metric, #1a73e8);
}

.bqa-metric-dd__opt-cmp {
  display: inline-block;
  margin-left: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #80868b;
}

.bqa-metric-bar__rating {
  background: #fff;
  border: 1px solid #dadce0;
  border-radius: 10px;
  padding: 10px 16px;
  min-width: 148px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.bqa-metric-bar__rating-label {
  font-size: 11px;
  font-weight: 700;
  color: #5f6368;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bqa-metric-bar__rating-value {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  color: #202124;
}

.bqa-metric-bar__rating-sub {
  font-size: 12px;
  color: #80868b;
}

.bqa-perf__compare-hint {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  align-items: center;
  margin: 0 0 10px;
  font-size: 12px;
}

.bqa-chart-panel__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.bqa-chart-panel__head h2 {
  margin: 0;
  font-size: 16px;
}

.bqa-chart-panel__sub {
  margin: 2px 0 0;
  color: var(--muted);
  font-size: 12px;
}

.bqa-period-a {
  color: #1a73e8;
  font-weight: 600;
}

.bqa-period-b {
  color: #5f6368;
  font-weight: 600;
}

.bqa-muted,
.bqa-chart-click-hint {
  margin: 0 0 8px;
  color: var(--muted);
  font-size: 12px;
}

.bqa-chart-wrap {
  position: relative;
  min-height: 220px;
}

.bqa-chart-wrap--perf {
  min-height: 280px;
  padding-top: 4px;
}

.bqa-chart-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  pointer-events: none;
}

.bqa-date-backdrop {
  position: fixed;
  inset: 0;
  z-index: 80;
  background: rgba(32, 33, 36, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.bqa-date-modal {
  width: min(440px, 96vw);
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 28px;
  box-shadow: 0 8px 28px rgba(32, 33, 36, 0.28);
  padding: 20px 20px 16px;
}

.bqa-date-modal__title {
  margin: 0 0 12px;
  font-size: 22px;
  font-weight: 400;
  color: #202124;
}

.bqa-date-tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #dadce0;
  margin-bottom: 8px;
}

.bqa-date-tab {
  flex: 1;
  border: 0;
  background: transparent;
  padding: 12px 8px;
  font-size: 14px;
  color: #5f6368;
  cursor: pointer;
  border-bottom: 3px solid transparent;
}

.bqa-date-tab.is-active {
  color: #1a73e8;
  font-weight: 600;
  border-bottom-color: #1a73e8;
}

.bqa-date-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 0 12px;
}

.bqa-date-radio {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 4px;
  cursor: pointer;
  font-size: 14px;
  color: #202124;
  line-height: 1.35;
}

.bqa-date-radio input {
  margin-top: 3px;
}

.bqa-date-custom {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-start;
  padding: 8px 4px 4px;
}

.bqa-outlined {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 150px;
}

.bqa-outlined > span {
  position: absolute;
  top: -8px;
  left: 10px;
  background: #fff;
  padding: 0 4px;
  font-size: 11px;
  color: #5f6368;
}

.bqa-outlined input {
  height: 48px;
  border: 1px solid #dadce0;
  border-radius: 4px;
  padding: 0 12px;
  font-size: 14px;
  color: #202124;
  background: #fff;
}

.bqa-outlined small {
  color: #5f6368;
  font-size: 11px;
  padding-left: 2px;
}

.bqa-date-clear {
  align-self: flex-start;
  margin-top: 8px;
  border: 0;
  background: transparent;
  color: #d93025;
  font-size: 13px;
  cursor: pointer;
  padding: 6px 4px;
}

.bqa-date-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
}

.bqa-date-cancel {
  border: 0;
  background: transparent;
  color: #1a73e8;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 999px;
  cursor: pointer;
}

.bqa-date-apply {
  border: 0;
  background: #1a73e8;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 22px;
  border-radius: 999px;
  cursor: pointer;
}

.bqa-date-apply:disabled {
  background: #e8eaed;
  color: #80868b;
  cursor: default;
}

.bqa-filters__row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 18px;
  align-items: flex-end;
}

.bqa-filters__row--secondary {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eef2f6;
  align-items: center;
}

.bqa-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.bqa-field--grow { flex: 1 1 280px; }
.bqa-field__label {
  font-size: 11px;
  font-weight: 600;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bqa-dates { display: flex; align-items: center; gap: 8px; }
.bqa-dates__sep { color: var(--muted); }

.bqa-input {
  height: 36px;
  border: 1px solid #d5dde8;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  background: #fff;
  color: var(--text);
}

.bqa-input--search { flex: 1; min-width: 180px; }
.bqa-select { min-width: 220px; }
.bqa-select--sm { min-width: 72px; height: 32px; }

.bqa-search { display: flex; gap: 8px; width: 100%; }

.bqa-pills { display: flex; flex-wrap: wrap; gap: 6px; }

.bqa-pill {
  border: 1px solid #d5dde8;
  background: #fff;
  color: #334155;
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 13px;
  cursor: pointer;
}

.bqa-pill.is-active {
  background: var(--blue);
  border-color: var(--blue);
  color: #fff;
}

.bqa-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #334155;
  cursor: pointer;
  user-select: none;
}

.bqa-select--rating { min-width: 160px; }

.bqa-rating-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff7e8;
  color: #8a5a00;
  font-size: 11px;
  font-weight: 700;
}
.bqa-rating-chip--ai {
  background: #ecfdf5;
  color: #047857;
}
.bqa-rating-chip--agree {
  background: #e6f4ea;
  color: #137333;
}
.bqa-rating-chip--disagree {
  background: #fce8e6;
  color: #c5221f;
}
.bqa-dialog-id {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  background: #eef2f7;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
}
.bqa-dialog-id--meta {
  background: transparent;
  padding: 0;
  color: #0f172a;
}
.bqa-tag-chip--handoff {
  background: #fff7ed;
  color: #c2410c;
}
.bqa-qc-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin: 4px 0 6px;
}
.bqa-qc-disagree {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 320px;
  margin-top: 4px;
}
.bqa-qc-reason {
  width: 100%;
  min-height: 56px;
  resize: vertical;
  font-size: 13px;
  line-height: 1.35;
}
.bqa-stars--sm .bqa-star {
  width: 26px;
  height: 26px;
  font-size: 12px;
}
.bqa-btn.is-active-soft {
  border-color: #1a73e8;
  color: #1a73e8;
  background: #e8f0fe;
}
.bqa-bubble.is-manager {
  background: #fff7ed;
  border-color: #fdba74;
  align-self: flex-start;
}
.bqa-muted {
  color: #64748b;
  font-size: 12px;
}

.bqa-tag-chip.is-active,
.bqa-tag-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 11px;
  font-weight: 600;
}

.bqa-tag-chip--ai {
  background: #ecfdf5;
  color: #047857;
  cursor: pointer;
}
.bqa-tag-chip--ai:hover {
  background: #d1fae5;
}
.bqa-tag-chip--manual {
  background: #eef2ff;
  color: #3730a3;
}
.bqa-tag-toggle--manual.is-active {
  background: #4338ca;
  color: #fff;
  border-color: #4338ca;
}

.bqa-modal__box--wide {
  width: min(960px, 96vw);
  max-height: 92vh;
}

.bqa-eval-config {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0 4px 8px;
}

.bqa-prompt {
  width: 100%;
  min-height: 280px;
  border: 1px solid #d5dde8;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.45;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  resize: vertical;
  background: #fff;
  color: var(--text);
}

.bqa-eval-hint {
  margin: 0;
  font-size: 12px;
  color: var(--muted);
  line-height: 1.4;
}

.bqa-ai-tags-editor,
.bqa-ai-tag-add {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bqa-ai-tag-row,
.bqa-ai-tag-add {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.bqa-input--grow {
  flex: 1 1 180px;
  min-width: 140px;
}

.bqa-eval-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding-top: 4px;
  border-top: 1px solid #eef2f6;
}

.bqa-annotate {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 14px;
  margin: 0 0 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #f8fafc;
}

.bqa-annotate__block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bqa-annotate__label {
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bqa-stars {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.bqa-star {
  width: 30px;
  height: 30px;
  border: 1px solid #d5dde8;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.bqa-star.is-active {
  background: #111827;
  border-color: #111827;
  color: #fff;
}

.bqa-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.bqa-tag-toggle {
  border: 1px solid #d5dde8;
  background: #fff;
  color: #334155;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
}

.bqa-tag-toggle.is-active {
  background: #1d4ed8;
  border-color: #1d4ed8;
  color: #fff;
}

.bqa-ai-tags-view {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.bqa-meta {
  margin-left: auto;
  font-size: 12px;
  color: var(--muted);
}

.bqa-btn {
  border: 0;
  border-radius: 8px;
  height: 36px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  white-space: nowrap;
}

.bqa-btn:disabled { opacity: 0.55; cursor: default; }
.bqa-btn--primary { background: var(--blue); color: #fff; }
.bqa-btn--accent { background: #111827; color: #fff; }
.bqa-btn--ghost {
  background: #fff;
  color: #334155;
  border: 1px solid #d5dde8;
}
.bqa-btn--link {
  background: #fff7e8;
  color: #8a5a00;
  border: 1px solid #f0dfb4;
}
.bqa-btn--crm {
  background: #eef6ff;
  color: #1d4ed8;
  border: 1px solid #c7dbff;
}
.bqa-btn--sm {
  height: 30px;
  padding: 0 10px;
  font-size: 12px;
}

.bqa-order-link {
  color: #1d4ed8;
  text-decoration: none;
  font-weight: 600;
}
.bqa-order-link:hover { text-decoration: underline; }

.bqa-order-status {
  display: inline-block;
  margin-top: 2px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
  font-size: 11px;
  font-weight: 600;
  width: fit-content;
}

.bqa-reasons { padding: 4px 8px; }
.bqa-reasons__head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  padding: 10px 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  cursor: pointer;
  text-align: left;
  border-radius: 8px;
}
.bqa-reasons__head:hover { background: #f5f8fc; }
.bqa-reasons__count {
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  background: #f1f5f9;
  border-radius: 999px;
  padding: 2px 8px;
}
.bqa-reasons__chevron {
  color: var(--muted);
  font-size: 12px;
  width: 14px;
  text-align: center;
}
.bqa-reasons__list {
  padding: 0 8px 10px;
}

.bqa-reason {
  width: 100%;
  display: grid;
  grid-template-columns: 200px 1fr 40px;
  gap: 10px;
  align-items: center;
  border: 0;
  background: transparent;
  padding: 6px 4px;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
}

.bqa-reason:hover,
.bqa-reason.is-active { background: #f5f8fc; }

.bqa-reason__name { font-size: 13px; color: #1e3a8a; }
.bqa-reason__bar {
  height: 8px;
  background: #eef2f7;
  border-radius: 99px;
  overflow: hidden;
}
.bqa-reason__bar i {
  display: block;
  height: 100%;
  background: #ef6b6b;
  border-radius: 99px;
}
.bqa-reason__cnt {
  font-size: 13px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.bqa-list-panel { overflow: hidden; }
.bqa-list-panel__head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 14px 16px 8px;
}
.bqa-list-panel__head h2 {
  margin: 0;
  font-size: 16px;
}
.bqa-list-panel__count {
  color: var(--muted);
  font-size: 13px;
}

.bqa-empty {
  padding: 36px 16px;
  text-align: center;
  color: var(--muted);
  font-size: 14px;
}

.bqa-item {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 16px;
  padding: 14px 16px;
  border-top: 1px solid var(--line);
  cursor: pointer;
  transition: background 0.12s ease;
}

.bqa-item:hover { background: #f8fafc; }
.bqa-item--fail { background: #fffbfb; }
.bqa-item--success { background: #fbfffd; }
.bqa-item--consult { background: #f8fbff; }
.bqa-item--exclude { opacity: 0.78; }

.bqa-item__top {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
}

.bqa-item__date,
.bqa-item__ai {
  font-size: 12px;
  color: var(--muted);
}

.bqa-item__ai--wait { color: #7c3aed; }

.bqa-item__summary {
  margin: 0 0 4px;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 600;
}

.bqa-item__preview {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.bqa-item__warn {
  margin-top: 6px;
  font-size: 12px;
  color: var(--warn);
}

.bqa-item__side {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
}

.bqa-item__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  color: var(--muted);
}

.bqa-item__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.bqa-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.bqa-badge--success { background: #d8f5e5; color: var(--ok); }
.bqa-badge--consult { background: #dbeafe; color: #1d4ed8; }
.bqa-badge--fail { background: #ffe1e1; color: var(--bad); }
.bqa-badge--pending { background: #eee7ff; color: #5b21b6; }
.bqa-badge--exclude { background: #e8eaee; color: #555; }

.bqa-trigger {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}
.bqa-trigger--purchase { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
.bqa-trigger--warranty { background: #fff7ed; color: #c2410c; border: 1px solid #fed7aa; }
.bqa-trigger--non_target { background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; }

.bqa-pager {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px 16px;
  border-top: 1px solid var(--line);
  font-size: 13px;
  color: var(--muted);
}

.bqa-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
}

.bqa-modal__box {
  width: min(880px, 100%);
  max-height: 92vh;
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  padding: 18px 18px 22px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.22);
}

.bqa-modal__head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.bqa-modal__head h2 {
  margin: 0 0 6px;
  font-size: 20px;
}

.bqa-modal__summary {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.4;
}

.bqa-digest {
  margin: 0 0 14px;
  padding: 12px 14px;
  background: #f7f8fb;
  border: 1px solid #e6e9f0;
  border-radius: 10px;
  display: grid;
  gap: 10px;
}
.bqa-digest__row p,
.bqa-digest__row ul {
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #222;
}
.bqa-digest__row ul {
  padding-left: 18px;
}
.bqa-digest__label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #6b7280;
}
.bqa-digest__row--warn {
  background: #fff7ed;
  margin: -4px -6px;
  padding: 8px 6px;
  border-radius: 8px;
}
.bqa-item__result {
  color: #6b7280;
  font-weight: 400;
}

.bqa-modal__close {
  border: 0;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  color: #667;
  cursor: pointer;
}

.bqa-modal__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin: 12px 0;
  font-size: 13px;
  color: #334155;
}

.bqa-modal__meta a {
  color: var(--blue);
  text-decoration: none;
}

.bqa-modal__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 14px;
}

.bqa-copy-hint {
  font-size: 12px;
  color: var(--ok);
  font-weight: 600;
}

.bqa-chat {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bqa-bubble {
  max-width: 92%;
  border-radius: 12px;
  padding: 10px 12px;
  line-height: 1.4;
  white-space: pre-wrap;
}

.bqa-bubble.is-user {
  align-self: flex-end;
  background: var(--blue-soft);
}

.bqa-bubble.is-bot {
  align-self: flex-start;
  background: #f5f7fa;
  border: 1px solid var(--line);
}

.bqa-bubble__meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 11px;
  color: var(--muted);
  margin-bottom: 4px;
}

.bqa-bubble__text { font-size: 14px; }

.bqa-toast {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  background: #111827;
  color: #fff;
  padding: 10px 16px;
  border-radius: 999px;
  font-size: 13px;
  z-index: 1400;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

@media (max-width: 860px) {
  .bqa {
    padding: 14px 12px 40px;
  }
  .bqa-top { flex-direction: column; }
  .bqa-metric-dd__panel {
    flex-direction: column;
    width: min(420px, 94vw);
    max-width: 94vw;
    max-height: 70vh;
    overflow: auto;
  }
  .bqa-metric-dd__col {
    min-width: 0;
    border-right: 0;
    border-bottom: 1px solid #eef0f3;
  }
  .bqa-metric-dd__col:last-child { border-bottom: 0; }
  .bqa-item { grid-template-columns: 1fr; }
  .bqa-reason { grid-template-columns: 1fr 48px; }
  .bqa-reason__bar { display: none; }
  .bqa-meta { margin-left: 0; width: 100%; }
}
</style>
